/**
 * Operations Data - Centralized Module
 * 
 * PURPOSE: Centralize all operations page data that was previously
 * hardcoded in individual JSX files.
 * 
 * ICON MAPPING: Icons are stored as string keys (e.g., 'Target', 'Clock')
 * and must be mapped to actual React components in the consuming component.
 * 
 * STRUCTURE:
 * - mineAccess: MineAccess.jsx data
 * - oreStocks: OreStocks.jsx data
 * - plantRecovery: PlantRecovery.jsx data
 * - productionRevenue: ProductionRevenue.jsx data
 * - efficiency: OperationalEfficiency.jsx data
 * - riskDelivery: RiskDelivery.jsx data
 * 
 * CONTRACT: Output structure must be IDENTICAL to what each page
 * currently consumes. Zero component changes required.
 */

// ============================================================================
// ICON MAPPING UTILITY
// Components should use this to convert icon keys to actual components
// ============================================================================
export const ICON_KEYS = {
  Target: 'Target',
  Clock: 'Clock',
  AlertTriangle: 'AlertTriangle',
  Layers: 'Layers',
  Shield: 'Shield',
  TrendingUp: 'TrendingUp',
  TrendingDown: 'TrendingDown',
  BarChart3: 'BarChart3',
  Truck: 'Truck',
  Mountain: 'Mountain',
  DollarSign: 'DollarSign',
  AlertCircle: 'AlertCircle',
  CheckCircle: 'CheckCircle',
  FileText: 'FileText',
  Info: 'Info',
  Crosshair: 'Crosshair',
  Pickaxe: 'Pickaxe',
};

// ============================================================================
// MINE ACCESS DATA
// ============================================================================
export const mineAccessTrajectoryKpis = {
  2026: [
    {
      key: 'esterilRemovido',
      label: { pt: 'Estéril Removido', en: 'Waste Removed' },
      unit: 'K m³',
      rollingData: [
        { month: 'Abr 25', value: 175 }, { month: 'Mai 25', value: 182 }, { month: 'Jun 25', value: 190 },
        { month: 'Jul 25', value: 165 }, { month: 'Ago 25', value: 178 }, { month: 'Set 25', value: 195 },
        { month: 'Out 25', value: 170 }, { month: 'Nov 25', value: 180 }, { month: 'Dez 25', value: 155 },
        { month: 'Jan 26', value: 168 }, { month: 'Fev 26', value: 172 }, { month: 'Mar 26', value: 180 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 195 }, { quarter: '3T26', value: 190 }, { quarter: '4T26', value: 185 }],
      q1Actual: 173,
      trend: 'stable',
    },
    {
      key: 'minerioExtraido',
      label: { pt: 'Minério Extraído', en: 'Ore Extracted' },
      unit: 'K m³',
      rollingData: [
        { month: 'Abr 25', value: 95 }, { month: 'Mai 25', value: 98 }, { month: 'Jun 25', value: 102 },
        { month: 'Jul 25', value: 88 }, { month: 'Ago 25', value: 95 }, { month: 'Set 25', value: 105 },
        { month: 'Out 25', value: 92 }, { month: 'Nov 25', value: 97 }, { month: 'Dez 25', value: 85 },
        { month: 'Jan 26', value: 92 }, { month: 'Fev 26', value: 95 }, { month: 'Mar 26', value: 98 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 105 }, { quarter: '3T26', value: 102 }, { quarter: '4T26', value: 100 }],
      q1Actual: 95,
      trend: 'stable',
    },
    {
      key: 'stripRatio',
      label: { pt: 'Strip Ratio', en: 'Strip Ratio' },
      unit: ':1',
      rollingData: [
        { month: 'Abr 25', value: 1.84 }, { month: 'Mai 25', value: 1.86 }, { month: 'Jun 25', value: 1.86 },
        { month: 'Jul 25', value: 1.88 }, { month: 'Ago 25', value: 1.87 }, { month: 'Set 25', value: 1.86 },
        { month: 'Out 25', value: 1.85 }, { month: 'Nov 25', value: 1.86 }, { month: 'Dez 25', value: 1.82 },
        { month: 'Jan 26', value: 1.83 }, { month: 'Fev 26', value: 1.81 }, { month: 'Mar 26', value: 1.84 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 1.86 }, { quarter: '3T26', value: 1.86 }, { quarter: '4T26', value: 1.85 }],
      q1Actual: 1.82,
      trend: 'stable',
    },
    {
      key: 'perfuracao',
      label: { pt: 'Perfuração', en: 'Drilling' },
      unit: 'K m',
      rollingData: [
        { month: 'Abr 25', value: 4.6 }, { month: 'Mai 25', value: 4.8 }, { month: 'Jun 25', value: 5.1 },
        { month: 'Jul 25', value: 4.2 }, { month: 'Ago 25', value: 4.5 }, { month: 'Set 25', value: 5.2 },
        { month: 'Out 25', value: 4.4 }, { month: 'Nov 25', value: 4.7 }, { month: 'Dez 25', value: 3.9 },
        { month: 'Jan 26', value: 4.0 }, { month: 'Fev 26', value: 4.2 }, { month: 'Mar 26', value: 4.3 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 4.9 }, { quarter: '3T26', value: 4.8 }, { quarter: '4T26', value: 4.7 }],
      q1Actual: 4.17,
      trend: 'down',
    },
  ],
  2025: [
    {
      key: 'esterilRemovido',
      label: { pt: 'Estéril Removido', en: 'Waste Removed' },
      unit: 'K m³',
      rollingData: [
        { month: 'Jan', value: 170 }, { month: 'Fev', value: 175 }, { month: 'Mar', value: 182 },
        { month: 'Abr', value: 178 }, { month: 'Mai', value: 185 }, { month: 'Jun', value: 190 },
        { month: 'Jul', value: 168 }, { month: 'Ago', value: 180 }, { month: 'Set', value: 188 },
        { month: 'Out', value: 175 }, { month: 'Nov', value: 182 }, { month: 'Dez', value: 167 },
      ],
      priorYearData: [
        { month: 'Jan', value: 158 }, { month: 'Fev', value: 162 }, { month: 'Mar', value: 168 },
        { month: 'Abr', value: 165 }, { month: 'Mai', value: 172 }, { month: 'Jun', value: 178 },
        { month: 'Jul', value: 155 }, { month: 'Ago', value: 168 }, { month: 'Set', value: 175 },
        { month: 'Out', value: 162 }, { month: 'Nov', value: 170 }, { month: 'Dez', value: 156 },
      ],
      trend: 'up',
    },
    {
      key: 'minerioExtraido',
      label: { pt: 'Minério Extraído', en: 'Ore Extracted' },
      unit: 'K m³',
      rollingData: [
        { month: 'Jan', value: 92 }, { month: 'Fev', value: 95 }, { month: 'Mar', value: 98 },
        { month: 'Abr', value: 95 }, { month: 'Mai', value: 100 }, { month: 'Jun', value: 102 },
        { month: 'Jul', value: 90 }, { month: 'Ago', value: 98 }, { month: 'Set', value: 103 },
        { month: 'Out', value: 96 }, { month: 'Nov', value: 99 }, { month: 'Dez', value: 92 },
      ],
      priorYearData: [
        { month: 'Jan', value: 85 }, { month: 'Fev', value: 88 }, { month: 'Mar', value: 91 },
        { month: 'Abr', value: 88 }, { month: 'Mai', value: 92 }, { month: 'Jun', value: 95 },
        { month: 'Jul', value: 83 }, { month: 'Ago', value: 90 }, { month: 'Set', value: 96 },
        { month: 'Out', value: 89 }, { month: 'Nov', value: 92 }, { month: 'Dez', value: 86 },
      ],
      trend: 'up',
    },
    {
      key: 'stripRatio',
      label: { pt: 'Strip Ratio', en: 'Strip Ratio' },
      unit: ':1',
      rollingData: [
        { month: 'Jan', value: 1.85 }, { month: 'Fev', value: 1.84 }, { month: 'Mar', value: 1.86 },
        { month: 'Abr', value: 1.87 }, { month: 'Mai', value: 1.85 }, { month: 'Jun', value: 1.86 },
        { month: 'Jul', value: 1.87 }, { month: 'Ago', value: 1.84 }, { month: 'Set', value: 1.83 },
        { month: 'Out', value: 1.82 }, { month: 'Nov', value: 1.84 }, { month: 'Dez', value: 1.82 },
      ],
      priorYearData: [
        { month: 'Jan', value: 1.86 }, { month: 'Fev', value: 1.84 }, { month: 'Mar', value: 1.85 },
        { month: 'Abr', value: 1.88 }, { month: 'Mai', value: 1.87 }, { month: 'Jun', value: 1.87 },
        { month: 'Jul', value: 1.87 }, { month: 'Ago', value: 1.87 }, { month: 'Set', value: 1.82 },
        { month: 'Out', value: 1.82 }, { month: 'Nov', value: 1.85 }, { month: 'Dez', value: 1.81 },
      ],
      trend: 'stable',
    },
    {
      key: 'perfuracao',
      label: { pt: 'Perfuração', en: 'Drilling' },
      unit: 'K m',
      rollingData: [
        { month: 'Jan', value: 4.5 }, { month: 'Fev', value: 4.7 }, { month: 'Mar', value: 4.8 },
        { month: 'Abr', value: 4.6 }, { month: 'Mai', value: 4.8 }, { month: 'Jun', value: 5.0 },
        { month: 'Jul', value: 4.3 }, { month: 'Ago', value: 4.6 }, { month: 'Set', value: 5.1 },
        { month: 'Out', value: 4.5 }, { month: 'Nov', value: 4.8 }, { month: 'Dez', value: 4.3 },
      ],
      priorYearData: [
        { month: 'Jan', value: 4.2 }, { month: 'Fev', value: 4.4 }, { month: 'Mar', value: 4.5 },
        { month: 'Abr', value: 4.3 }, { month: 'Mai', value: 4.5 }, { month: 'Jun', value: 4.7 },
        { month: 'Jul', value: 4.0 }, { month: 'Ago', value: 4.3 }, { month: 'Set', value: 4.8 },
        { month: 'Out', value: 4.2 }, { month: 'Nov', value: 4.5 }, { month: 'Dez', value: 4.1 },
      ],
      trend: 'stable',
    },
  ],
};

export const mineAccessData = {
  2026: {
    period: '1T 2026',
    keyMessages: [
      {
        iconKey: 'Target',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'ACESSO', en: 'ACCESS' },
        text: {
          pt: 'Atrasos em estéril ou perfuração comprometem alimentação futura da planta, custo unitário e previsibilidade da geração de caixa.',
          en: 'Delays in waste or drilling compromise future plant feed, unit cost and cash generation predictability.'
        }
      },
      {
        iconKey: 'Clock',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'PREPARAÇÃO', en: 'PREPARATION' },
        text: {
          pt: 'A produção corrente pode mascarar atraso de preparação. A operação pode proteger o trimestre via stockpile, mas isso não elimina fragilidade na frente mineira.',
          en: 'Current production may mask preparation delays. Operations can protect the quarter via stockpile, but this does not eliminate fragility at the mining front.'
        }
      },
      {
        iconKey: 'AlertTriangle',
        iconBg: 'bg-rose-500/20',
        iconColor: 'text-rose-400',
        titleColor: 'text-rose-400',
        title: { pt: 'RISCO', en: 'RISK' },
        text: {
          pt: 'Os desvios nesta área afectam flexibilidade operacional, dependência de stockpile e robustez da entrega. Leitura de prontidão física e risco financeiro futuro.',
          en: 'Deviations in this area affect operational flexibility, stockpile dependency and delivery robustness. Reading of physical readiness and future financial risk.'
        }
      }
    ],
    kpis: {
      minerioExtraido: { actual: 285000, plan: 300000, variance: -5.0, yoyVariance: -2.1, unit: 'm³', trend: 'down' },
      esterilRemovido: { actual: 520000, plan: 550000, variance: -5.5, yoyVariance: -3.2, unit: 'm³', trend: 'down' },
      stripRatio: { actual: 1.82, plan: 1.83, variance: -0.5, yoyVariance: -1.1, unit: ':1', trend: 'stable' },
      perfuracaoProducao: { actual: 12500, plan: 14000, variance: -10.7, yoyVariance: -8.3, unit: 'm', trend: 'down' },
    },
    executiveSummary: {
      pt: `**Leitura do período**: O 1T 2026 apresenta atraso na criação de acesso ao minério. Minério extraído -5% vs orçamento, estéril removido -5.5%, perfuração -10.7%. O strip ratio está alinhado (1.82:1), mas o ritmo de preparação não acompanha o plano.

**Principal risco operacional**: O atraso na perfuração (-10.7%) compromete a preparação de bancos para os trimestres seguintes. A frente de Mina 2 (M2) mostra pressão operacional que pode limitar acesso a zonas de maior teor.

**Ponte com finanças**: Os desvios criam pressão sobre custo unitário (menor diluição de fixos), aumentam dependência de stockpile e reduzem flexibilidade de blending. A trajectória actual exige recuperação no 2T para proteger a geração de caixa.`,
      en: `**Period reading**: Q1 2026 shows delay in ore access creation. Ore extracted -5% vs budget, waste removed -5.5%, drilling -10.7%. Strip ratio is aligned (1.82:1), but preparation pace does not follow the plan.

**Main operational risk**: Drilling delay (-10.7%) compromises bench preparation for coming quarters. Mine 2 (M2) front shows operational pressure that may limit access to higher grade zones.

**Finance bridge**: Deviations create pressure on unit cost (lower fixed dilution), increase stockpile dependency and reduce blending flexibility. Current trajectory requires Q2 recovery to protect cash generation.`
    },
    ritmoAbertura: {
      monthly: [
        { month: 'Jan', minerioReal: 92, minerioOrc: 100, esterilReal: 168, esterilOrc: 183 },
        { month: 'Fev', minerioReal: 95, minerioOrc: 100, esterilReal: 172, esterilOrc: 183 },
        { month: 'Mar', minerioReal: 98, minerioOrc: 100, esterilReal: 180, esterilOrc: 184 },
      ],
      ytd: {
        minerio: { real: 285, orc: 300, variance: -5.0, unit: 'K m³' },
        esteril: { real: 520, orc: 550, variance: -5.5, unit: 'K m³' },
        massaMineira: { real: 805, orc: 850, variance: -5.3, unit: 'K m³' },
        stripRatio: { real: 1.82, orc: 1.83, variance: -0.5, unit: ':1' },
      }
    },
    acessoCava: [
      { 
        id: 'M3', 
        name: 'Mina 3 (M3)', 
        status: 'conforme',
        desvioKpi: -3.2,
        evidence: { pt: 'Estéril removido -3.2% vs plano; desmonte conforme cronograma', en: 'Waste removed -3.2% vs plan; stripping as per schedule' },
        executiveReading: { pt: 'Frente principal a operar dentro da tolerância de 5%', en: 'Main front operating within 5% tolerance' },
        risk: 'low',
        notes: { pt: 'Sem alertas materiais nos relatórios mensais', en: 'No material alerts in monthly reports' }
      },
      { 
        id: 'M1', 
        name: 'Mina 1 (M1)', 
        status: 'acompanhamento',
        desvioKpi: -7.8,
        evidence: { pt: 'Estéril removido -7.8% vs plano; atraso na zona norte', en: 'Waste removed -7.8% vs plan; delay in north zone' },
        executiveReading: { pt: 'Desvio entre 5-10% — recuperação em curso, sem impacto imediato na alimentação', en: 'Deviation 5-10% — recovery in progress, no immediate feed impact' },
        risk: 'medium',
        notes: { pt: 'Reforço de equipamento em implementação', en: 'Equipment reinforcement being implemented' }
      },
      { 
        id: 'M2', 
        name: 'Mina 2 (M2)', 
        status: 'pressao',
        desvioKpi: -12.5,
        evidence: { pt: 'Estéril removido -12.5% vs plano em Março; acesso limitado', en: 'Waste removed -12.5% vs plan in March; limited access' },
        executiveReading: { pt: 'Desvio >10% — pressão operacional pode limitar acesso a zonas de maior teor', en: 'Deviation >10% — operational pressure may limit access to higher grade zones' },
        risk: 'high',
        notes: { pt: 'Tema prioritário em discussão com operações', en: 'Priority topic under discussion with operations' }
      },
      { 
        id: 'M4', 
        name: 'Mina 4 (M4)', 
        status: 'conforme',
        desvioKpi: -2.1,
        evidence: { pt: 'Preparação de bancos -2.1% vs cronograma; dentro da tolerância', en: 'Bench preparation -2.1% vs schedule; within tolerance' },
        executiveReading: { pt: 'Frente secundária conforme, desvio ≤5%', en: 'Secondary front on track, deviation ≤5%' },
        risk: 'low',
        notes: { pt: 'Monitorização normal', en: 'Normal monitoring' }
      },
    ],
    perfuracao: {
      monthly: [
        { month: 'Jan', real: 4000, plan: 4667 },
        { month: 'Fev', real: 4200, plan: 4667 },
        { month: 'Mar', real: 4300, plan: 4666 },
      ],
      ytd: { real: 12500, plan: 14000, variance: -10.7, percentOfPlan: 89.3 },
      readinessComment: {
        pt: 'Perfuração a 89.3% do plano trimestral — abaixo do limiar crítico de 90%. A preparação é insuficiente para sustentar o ritmo previsto no 2T sem dependência adicional de stockpile. Indica risco de prontidão para os próximos períodos.',
        en: 'Drilling at 89.3% of quarterly plan — below the critical 90% threshold. Preparation is insufficient to sustain the planned Q2 pace without additional stockpile dependency. Indicates readiness risk for coming periods.'
      },
      readinessStatus: 'critico'
    },
    ponteFinanceira: [
      { 
        desvio: { pt: 'Estéril removido -5.5% vs plano', en: 'Waste removed -5.5% vs plan' },
        implicacaoOperacional: { pt: 'Menor abertura de cava; acesso a novas zonas comprometido', en: 'Less pit opening; access to new zones compromised' },
        leituraFinanceira: { pt: 'Pressão sobre custo unitário e redução de flexibilidade de alimentação nos próximos trimestres', en: 'Pressure on unit cost and reduced feed flexibility in coming quarters' },
        severidade: 'medium'
      },
      { 
        desvio: { pt: 'Perfuração a 89.3% do plano (crítico)', en: 'Drilling at 89.3% of plan (critical)' },
        implicacaoOperacional: { pt: 'Preparação insuficiente de bancos para 2T e 3T', en: 'Insufficient bench preparation for Q2 and Q3' },
        leituraFinanceira: { pt: 'Maior dependência de stockpile; risco sobre continuidade operacional e previsibilidade de caixa', en: 'Higher stockpile dependency; risk on operational continuity and cash predictability' },
        severidade: 'high'
      },
      { 
        desvio: { pt: 'Strip ratio em linha (-0.5%)', en: 'Strip ratio aligned (-0.5%)' },
        implicacaoOperacional: { pt: 'Eficiência de movimentação dentro do planeado', en: 'Movement efficiency within planned' },
        leituraFinanceira: { pt: 'Sem pressão adicional sobre custo unitário de mina nesta dimensão', en: 'No additional pressure on mine unit cost in this dimension' },
        severidade: 'low'
      },
    ],
    watchlist: [
      { 
        risk: { pt: 'Atraso no desmonte - Mina 2 (M2)', en: 'Stripping delay - Mine 2 (M2)' },
        severity: 'high',
        evidence: { pt: 'Relatório Mensal Mar/26 - volume de estéril 15% abaixo do plano na frente', en: 'Monthly Report Mar/26 - waste volume 15% below plan at front' },
        implicacaoOperacional: { pt: 'Limitação de acesso a zonas de maior teor no Q2', en: 'Limited access to higher grade zones in Q2' },
        implicacaoFinanceira: { pt: 'Potencial pressão sobre teor de alimentação e custo unitário', en: 'Potential pressure on feed grade and unit cost' },
        mitigation: { pt: 'Reafectação de equipamento em discussão', en: 'Equipment reallocation under discussion' }
      },
      { 
        risk: { pt: 'Perfuração de produção abaixo do plano', en: 'Production drilling below plan' },
        severity: 'high',
        evidence: { pt: 'Acumulado 1T26: 12.5K m vs 14.0K m planeado (-10.7%)', en: 'YTD Q1 26: 12.5K m vs 14.0K m planned (-10.7%)' },
        implicacaoOperacional: { pt: 'Risco de preparação insuficiente para Q2', en: 'Risk of insufficient preparation for Q2' },
        implicacaoFinanceira: { pt: 'Maior dependência de stockpile; risco de ruptura operacional', en: 'Higher stockpile dependency; operational disruption risk' },
        mitigation: { pt: 'Avaliação de contratação de equipamento adicional', en: 'Evaluation of additional equipment hiring' }
      },
      { 
        risk: { pt: 'Concentração operacional em Mina 3', en: 'Operational concentration in Mine 3' },
        severity: 'medium',
        evidence: { pt: 'POA 2026 - 65% do minério planeado proveniente de M3', en: 'POA 2026 - 65% of planned ore from M3' },
        implicacaoOperacional: { pt: 'Flexibilidade limitada em caso de constrangimento na frente', en: 'Limited flexibility in case of front constraint' },
        implicacaoFinanceira: { pt: 'Risco de execução concentrado numa única cava', en: 'Execution risk concentrated in a single pit' },
        mitigation: { pt: 'Acompanhamento reforçado; desenvolvimento de alternativas', en: 'Enhanced monitoring; alternative development' }
      },
    ],
  },
  2025: {
    period: '2025',
    keyMessages: [
      {
        iconKey: 'Target',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'ACESSO', en: 'ACCESS' },
        text: {
          pt: 'O período revelou criação de acesso consistente. A operação sustentou a alimentação da planta sem rupturas, protegendo custo unitário e geração de caixa.',
          en: 'The period revealed consistent access creation. Operations sustained plant feed without disruptions, protecting unit cost and cash generation.'
        }
      },
      {
        iconKey: 'Clock',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'PREPARAÇÃO', en: 'PREPARATION' },
        text: {
          pt: 'A preparação acompanhou o plano anual, garantindo flexibilidade de blending e opcionalidade operacional para os períodos seguintes.',
          en: 'Preparation followed the annual plan, ensuring blending flexibility and operational optionality for coming periods.'
        }
      },
      {
        iconKey: 'AlertTriangle',
        iconBg: 'bg-rose-500/20',
        iconColor: 'text-rose-400',
        titleColor: 'text-rose-400',
        title: { pt: 'RISCO', en: 'RISK' },
        text: {
          pt: 'A execução sólida em 2025 reduziu o risco de acessibilidade, criando buffer operacional para absorver variabilidade futura.',
          en: 'Solid execution in 2025 reduced accessibility risk, creating operational buffer to absorb future variability.'
        }
      }
    ],
    kpis: {
      minerioExtraido: { actual: 1160000, plan: 1100000, variance: 5.5, yoyVariance: 8.2, unit: 'm³', trend: 'up' },
      esterilRemovido: { actual: 2140000, plan: 2050000, variance: 4.4, yoyVariance: 6.8, unit: 'm³', trend: 'up' },
      stripRatio: { actual: 1.84, plan: 1.86, variance: -1.1, yoyVariance: -0.5, unit: ':1', trend: 'stable' },
      perfuracaoProducao: { actual: 56200, plan: 55000, variance: 2.2, yoyVariance: 4.5, unit: 'm', trend: 'up' },
    },
    executiveSummary: {
      pt: `**Leitura do período**: 2025 fechou com execução sólida na criação de acesso. Minério extraído +5.5% vs orçamento, estéril removido +4.4%, perfuração +2.2%. Strip ratio controlado (1.84:1 vs 1.86:1 planeado).

**Desempenho operacional**: A operação conseguiu sustentar ritmo acima do planeado em todas as frentes principais. Mina 3 (M3) e Mina 2 (M2) entregaram acima das expectativas, criando buffer para 2026.

**Ponte com finanças**: A execução acima do plano protegeu custo unitário (maior diluição de fixos), garantiu flexibilidade de blending e reduziu dependência de stockpile. Contribuiu positivamente para a geração de caixa do período.`,
      en: `**Period reading**: 2025 closed with solid execution in access creation. Ore extracted +5.5% vs budget, waste removed +4.4%, drilling +2.2%. Strip ratio controlled (1.84:1 vs 1.86:1 planned).

**Operational performance**: Operations managed to sustain pace above plan at all main fronts. Mine 3 (M3) and Mine 2 (M2) delivered above expectations, creating buffer for 2026.

**Finance bridge**: Above-plan execution protected unit cost (higher fixed dilution), ensured blending flexibility and reduced stockpile dependency. Contributed positively to period cash generation.`
    },
    ritmoAbertura: {
      monthly: [
        { month: 'Jan', minerioReal: 92, minerioOrc: 88 }, { month: 'Fev', minerioReal: 95, minerioOrc: 90 },
        { month: 'Mar', minerioReal: 98, minerioOrc: 92 }, { month: 'Abr', minerioReal: 95, minerioOrc: 91 },
        { month: 'Mai', minerioReal: 100, minerioOrc: 93 }, { month: 'Jun', minerioReal: 102, minerioOrc: 95 },
        { month: 'Jul', minerioReal: 90, minerioOrc: 89 }, { month: 'Ago', minerioReal: 98, minerioOrc: 92 },
        { month: 'Set', minerioReal: 103, minerioOrc: 96 }, { month: 'Out', minerioReal: 96, minerioOrc: 92 },
        { month: 'Nov', minerioReal: 99, minerioOrc: 94 }, { month: 'Dez', minerioReal: 92, minerioOrc: 88 },
      ],
      ytd: {
        minerio: { real: 1160, orc: 1100, variance: 5.5, unit: 'K m³' },
        esteril: { real: 2140, orc: 2050, variance: 4.4, unit: 'K m³' },
        massaMineira: { real: 3300, orc: 3150, variance: 4.8, unit: 'K m³' },
        stripRatio: { real: 1.84, orc: 1.86, variance: -1.1, unit: ':1' },
      }
    },
    acessoCava: [
      { id: 'M3', name: 'Mina 3 (M3)', status: 'conforme', desvioKpi: 6.2, evidence: { pt: 'Estéril removido +6.2% vs plano; desmonte acima do cronograma', en: 'Waste removed +6.2% vs plan; stripping above schedule' }, executiveReading: { pt: 'Frente principal a operar acima do plano', en: 'Main front operating above plan' }, risk: 'low', notes: { pt: 'Execução exemplar', en: 'Exemplary execution' } },
      { id: 'M1', name: 'Mina 1 (M1)', status: 'conforme', desvioKpi: 3.5, evidence: { pt: 'Estéril removido +3.5% vs plano', en: 'Waste removed +3.5% vs plan' }, executiveReading: { pt: 'Dentro da tolerância positiva', en: 'Within positive tolerance' }, risk: 'low', notes: { pt: 'Monitorização normal', en: 'Normal monitoring' } },
      { id: 'M2', name: 'Mina 2 (M2)', status: 'conforme', desvioKpi: 4.8, evidence: { pt: 'Estéril removido +4.8% vs plano', en: 'Waste removed +4.8% vs plan' }, executiveReading: { pt: 'Execução sólida', en: 'Solid execution' }, risk: 'low', notes: { pt: 'Contribuiu para buffer 2026', en: 'Contributed to 2026 buffer' } },
      { id: 'M4', name: 'Mina 4 (M4)', status: 'conforme', desvioKpi: 2.1, evidence: { pt: 'Preparação de bancos +2.1% vs cronograma', en: 'Bench preparation +2.1% vs schedule' }, executiveReading: { pt: 'Frente secundária conforme', en: 'Secondary front on track' }, risk: 'low', notes: { pt: 'Monitorização normal', en: 'Normal monitoring' } },
    ],
    perfuracao: {
      monthly: [
        { month: 'Jan', real: 4500, plan: 4500 }, { month: 'Fev', real: 4700, plan: 4550 },
        { month: 'Mar', real: 4800, plan: 4600 }, { month: 'Abr', real: 4600, plan: 4550 },
        { month: 'Mai', real: 4800, plan: 4600 }, { month: 'Jun', real: 5000, plan: 4650 },
        { month: 'Jul', real: 4300, plan: 4400 }, { month: 'Ago', real: 4600, plan: 4550 },
        { month: 'Set', real: 5100, plan: 4700 }, { month: 'Out', real: 4500, plan: 4550 },
        { month: 'Nov', real: 4800, plan: 4600 }, { month: 'Dez', real: 4500, plan: 4750 },
      ],
      ytd: { real: 56200, plan: 55000, variance: 2.2, percentOfPlan: 102.2 },
      readinessComment: {
        pt: 'Perfuração a 102.2% do plano anual — acima do limiar de conformidade. A preparação é suficiente para sustentar o ritmo planeado em 2026 com margem de segurança.',
        en: 'Drilling at 102.2% of annual plan — above compliance threshold. Preparation is sufficient to sustain planned 2026 pace with safety margin.'
      },
      readinessStatus: 'conforme'
    },
    ponteFinanceira: [
      { desvio: { pt: 'Estéril removido +4.4% vs plano', en: 'Waste removed +4.4% vs plan' }, implicacaoOperacional: { pt: 'Maior abertura de cava; acesso a novas zonas garantido', en: 'Greater pit opening; access to new zones ensured' }, leituraFinanceira: { pt: 'Protecção de custo unitário e flexibilidade de alimentação', en: 'Unit cost protection and feed flexibility' }, severidade: 'low' },
      { desvio: { pt: 'Perfuração a 102.2% do plano', en: 'Drilling at 102.2% of plan' }, implicacaoOperacional: { pt: 'Preparação robusta de bancos para 2026', en: 'Robust bench preparation for 2026' }, leituraFinanceira: { pt: 'Menor risco de ruptura operacional; previsibilidade de caixa protegida', en: 'Lower operational disruption risk; cash predictability protected' }, severidade: 'low' },
      { desvio: { pt: 'Strip ratio controlado (-1.1%)', en: 'Strip ratio controlled (-1.1%)' }, implicacaoOperacional: { pt: 'Eficiência de movimentação optimizada', en: 'Optimized movement efficiency' }, leituraFinanceira: { pt: 'Contribuição positiva para custo unitário de mina', en: 'Positive contribution to mine unit cost' }, severidade: 'low' },
    ],
    watchlist: [],
  },
};

// ============================================================================
// ORE STOCKS DATA
// ============================================================================
export const oreStocksTrajectoryKpis = {
  2026: [
    {
      key: 'stockpile',
      label: { pt: 'Stockpile', en: 'Stockpile' },
      unit: 'K ton',
      rollingData: [
        { month: 'Abr 25', value: 310 }, { month: 'Mai 25', value: 305 }, { month: 'Jun 25', value: 315 },
        { month: 'Jul 25', value: 320 }, { month: 'Ago 25', value: 308 }, { month: 'Set 25', value: 295 },
        { month: 'Out 25', value: 302 }, { month: 'Nov 25', value: 298 }, { month: 'Dez 25', value: 290 },
        { month: 'Jan 26', value: 288 }, { month: 'Fev 26', value: 286 }, { month: 'Mar 26', value: 285 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 310 }, { quarter: '3T26', value: 320 }, { quarter: '4T26', value: 315 }],
      q1Actual: 285,
      trend: 'down',
    },
    {
      key: 'alimentacaoStockpile',
      label: { pt: 'Alimentação Stockpile', en: 'Stockpile Feed' },
      unit: '%',
      rollingData: [
        { month: 'Abr 25', value: 48 }, { month: 'Mai 25', value: 52 }, { month: 'Jun 25', value: 50 },
        { month: 'Jul 25', value: 55 }, { month: 'Ago 25', value: 58 }, { month: 'Set 25', value: 62 },
        { month: 'Out 25', value: 56 }, { month: 'Nov 25', value: 60 }, { month: 'Dez 25', value: 65 },
        { month: 'Jan 26', value: 58 }, { month: 'Fev 26', value: 60 }, { month: 'Mar 26', value: 62 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 50 }, { quarter: '3T26', value: 48 }, { quarter: '4T26', value: 45 }],
      q1Actual: 60,
      trend: 'up',
    },
    {
      key: 'alimentacaoNovoStock',
      label: { pt: 'Alimentação Novo Stock', en: 'New Stock Feed' },
      unit: 'K m³',
      rollingData: [
        { month: 'Abr 25', value: 320 }, { month: 'Mai 25', value: 335 }, { month: 'Jun 25', value: 328 },
        { month: 'Jul 25', value: 310 }, { month: 'Ago 25', value: 325 }, { month: 'Set 25', value: 340 },
        { month: 'Out 25', value: 315 }, { month: 'Nov 25', value: 308 }, { month: 'Dez 25', value: 295 },
        { month: 'Jan 26', value: 305 }, { month: 'Fev 26', value: 298 }, { month: 'Mar 26', value: 297 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 330 }, { quarter: '3T26', value: 340 }, { quarter: '4T26', value: 315 }],
      q1Actual: 300,
      trend: 'down',
    },
    {
      key: 'pesoStockpileBlend',
      label: { pt: 'Peso Stockpile no Blend', en: 'Stockpile Weight in Blend' },
      unit: '%',
      rollingData: [
        { month: 'Abr 25', value: 12.5 }, { month: 'Mai 25', value: 13.2 }, { month: 'Jun 25', value: 13.8 },
        { month: 'Jul 25', value: 14.5 }, { month: 'Ago 25', value: 15.0 }, { month: 'Set 25', value: 15.8 },
        { month: 'Out 25', value: 15.2 }, { month: 'Nov 25', value: 16.0 }, { month: 'Dez 25', value: 16.5 },
        { month: 'Jan 26', value: 16.2 }, { month: 'Fev 26', value: 16.5 }, { month: 'Mar 26', value: 16.7 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 14.5 }, { quarter: '3T26', value: 14.0 }, { quarter: '4T26', value: 14.3 }],
      q1Actual: 16.7,
      trend: 'up',
    },
  ],
  2025: [
    {
      key: 'stockpile',
      label: { pt: 'Stockpile', en: 'Stockpile' },
      unit: 'K ton',
      rollingData: [
        { month: 'Jan', value: 280 }, { month: 'Fev', value: 290 }, { month: 'Mar', value: 298 },
        { month: 'Abr', value: 310 }, { month: 'Mai', value: 305 }, { month: 'Jun', value: 315 },
        { month: 'Jul', value: 320 }, { month: 'Ago', value: 308 }, { month: 'Set', value: 295 },
        { month: 'Out', value: 302 }, { month: 'Nov', value: 298 }, { month: 'Dez', value: 290 },
      ],
      priorYearData: [
        { month: 'Jan', value: 265 }, { month: 'Fev', value: 272 }, { month: 'Mar', value: 278 },
        { month: 'Abr', value: 285 }, { month: 'Mai', value: 290 }, { month: 'Jun', value: 295 },
        { month: 'Jul', value: 300 }, { month: 'Ago', value: 292 }, { month: 'Set', value: 280 },
        { month: 'Out', value: 275 }, { month: 'Nov', value: 270 }, { month: 'Dez', value: 268 },
      ],
      trend: 'stable',
    },
    {
      key: 'alimentacaoStockpile',
      label: { pt: 'Alimentação Stockpile', en: 'Stockpile Feed' },
      unit: '%',
      rollingData: [
        { month: 'Jan', value: 45 }, { month: 'Fev', value: 48 }, { month: 'Mar', value: 46 },
        { month: 'Abr', value: 48 }, { month: 'Mai', value: 52 }, { month: 'Jun', value: 50 },
        { month: 'Jul', value: 55 }, { month: 'Ago', value: 58 }, { month: 'Set', value: 62 },
        { month: 'Out', value: 56 }, { month: 'Nov', value: 60 }, { month: 'Dez', value: 65 },
      ],
      priorYearData: [
        { month: 'Jan', value: 42 }, { month: 'Fev', value: 44 }, { month: 'Mar', value: 43 },
        { month: 'Abr', value: 45 }, { month: 'Mai', value: 48 }, { month: 'Jun', value: 47 },
        { month: 'Jul', value: 50 }, { month: 'Ago', value: 52 }, { month: 'Set', value: 55 },
        { month: 'Out', value: 53 }, { month: 'Nov', value: 56 }, { month: 'Dez', value: 58 },
      ],
      trend: 'up',
    },
    {
      key: 'alimentacaoNovoStock',
      label: { pt: 'Alimentação Novo Stock', en: 'New Stock Feed' },
      unit: 'K m³',
      rollingData: [
        { month: 'Jan', value: 310 }, { month: 'Fev', value: 318 }, { month: 'Mar', value: 325 },
        { month: 'Abr', value: 320 }, { month: 'Mai', value: 335 }, { month: 'Jun', value: 328 },
        { month: 'Jul', value: 310 }, { month: 'Ago', value: 325 }, { month: 'Set', value: 340 },
        { month: 'Out', value: 315 }, { month: 'Nov', value: 308 }, { month: 'Dez', value: 295 },
      ],
      priorYearData: [
        { month: 'Jan', value: 290 }, { month: 'Fev', value: 295 }, { month: 'Mar', value: 305 },
        { month: 'Abr', value: 300 }, { month: 'Mai', value: 315 }, { month: 'Jun', value: 310 },
        { month: 'Jul', value: 295 }, { month: 'Ago', value: 308 }, { month: 'Set', value: 320 },
        { month: 'Out', value: 298 }, { month: 'Nov', value: 290 }, { month: 'Dez', value: 285 },
      ],
      trend: 'stable',
    },
    {
      key: 'pesoStockpileBlend',
      label: { pt: 'Peso Stockpile no Blend', en: 'Stockpile Weight in Blend' },
      unit: '%',
      rollingData: [
        { month: 'Jan', value: 10.5 }, { month: 'Fev', value: 11.2 }, { month: 'Mar', value: 11.8 },
        { month: 'Abr', value: 12.5 }, { month: 'Mai', value: 13.2 }, { month: 'Jun', value: 13.8 },
        { month: 'Jul', value: 14.5 }, { month: 'Ago', value: 15.0 }, { month: 'Set', value: 15.8 },
        { month: 'Out', value: 15.2 }, { month: 'Nov', value: 16.0 }, { month: 'Dez', value: 16.5 },
      ],
      priorYearData: [
        { month: 'Jan', value: 9.0 }, { month: 'Fev', value: 9.5 }, { month: 'Mar', value: 10.0 },
        { month: 'Abr', value: 10.5 }, { month: 'Mai', value: 11.0 }, { month: 'Jun', value: 11.5 },
        { month: 'Jul', value: 12.0 }, { month: 'Ago', value: 12.5 }, { month: 'Set', value: 12.8 },
        { month: 'Out', value: 12.2 }, { month: 'Nov', value: 12.5 }, { month: 'Dez', value: 12.8 },
      ],
      trend: 'up',
    },
  ],
};

export const oreStocksData = {
  2026: {
    period: '1T 2026',
    // Mensagens-Chave CFO-oriented (3 mensagens)
    keyMessages: [
      {
        iconKey: 'Package',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'ALMOFADA OPERACIONAL', en: 'OPERATIONAL BUFFER' },
        text: {
          pt: 'O stockpile é uma almofada operacional, mas o seu consumo excessivo pode mascarar fragilidade na frente mineira.',
          en: 'Stockpile is an operational buffer, but excessive consumption may mask weakness at the mining front.'
        }
      },
      {
        iconKey: 'Layers',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'VOLUME E QUALIDADE', en: 'VOLUME AND QUALITY' },
        text: {
          pt: 'O stock não deve ser lido apenas em volume, mas também em qualidade e composição do feed que alimenta a planta.',
          en: 'Stock should not be read only in volume, but also in quality and composition of the feed that supplies the plant.'
        }
      },
      {
        iconKey: 'TrendingUp',
        iconBg: 'bg-blue-500/20',
        iconColor: 'text-blue-400',
        titleColor: 'text-blue-400',
        title: { pt: 'CONTINUIDADE E CAIXA', en: 'CONTINUITY AND CASH' },
        text: {
          pt: 'Para o CFO, esta página deve mostrar se o stock está a proteger continuidade e caixa ou a transferir risco para períodos seguintes.',
          en: 'For the CFO, this page should show if stock is protecting continuity and cash or transferring risk to future periods.'
        }
      }
    ],
    // 4 KPIs OFICIAIS (suportados por relatórios)
    kpis: {
      stockpile: { actual: 285000, plan: 300000, variance: -5.0, yoyVariance: 3.2, unit: 'm³', trend: 'down' },
      alimentacaoStockpile: { actual: 180000, plan: 165000, variance: 9.1, yoyVariance: 12.5, unit: 'm³', trend: 'up' },
      alimentacaoNovoStock: { actual: 900000, plan: 985000, variance: -8.6, yoyVariance: -2.1, unit: 'm³', trend: 'down' },
      pesoStockpileBlend: { actual: 16.7, plan: 14.3, variance: 16.8, yoyVariance: 8.4, unit: '%', trend: 'up' },
    },
    // Bloco 1: Posição e Evolução do Stockpile
    posicaoEvolucao: {
      abertura: { value: 310000, unit: 'm³' },
      fecho: { value: 285000, unit: 'm³' },
      variacao: { value: -25000, percentagem: -8.1, unit: 'm³' },
      monthly: [
        { month: 'Jan', stockpile: 310, consumo: 65, entrada: 55 },
        { month: 'Fev', stockpile: 300, consumo: 60, entrada: 50 },
        { month: 'Mar', stockpile: 285, consumo: 55, entrada: 40 },
      ],
      levantamento: {
        pt: 'Último levantamento por drone: 278K m³ (Março 2026). Diferença vs registo contabilístico: 2.5%.',
        en: 'Last drone survey: 278K m³ (March 2026). Difference vs accounting record: 2.5%.'
      }
    },
    // Bloco 2: Consumo de Stockpile e Dependência Operacional
    consumoDependencia: {
      alimentacaoStockpile: { real: 180, orc: 165, variance: 9.1, unit: 'K m³' },
      alimentacaoNovoStock: { real: 900, orc: 985, variance: -8.6, unit: 'K m³' },
      pesoNoBlend: { real: 16.7, orc: 14.3, variance: 16.8, unit: '%' },
      leituraExecutiva: {
        pt: 'A operação consumiu mais stockpile do que previsto (+9.1%), compensando parcialmente o menor ritmo de extracção de minério novo (-8.6%). O peso do stockpile no blend subiu para 16.7% vs 14.3% planeado, indicando maior dependência de stock acumulado.',
        en: 'Operations consumed more stockpile than planned (+9.1%), partially offsetting lower new ore extraction pace (-8.6%). Stockpile weight in blend rose to 16.7% vs 14.3% planned, indicating higher dependency on accumulated stock.'
      }
    },
    // Bloco 3: Qualidade do Stock e Impacto no Feed
    qualidadeStock: {
      comentarioQualidade: {
        pt: 'O stockpile actual é maioritariamente composto por material de Mina 3 (estimativa: ~60%) e material low-grade de reprocessamento (~25%). A presença de material histórico e oversize foi referida nos relatórios de Fevereiro e Março como factor de atenção para o blend.',
        en: 'Current stockpile is mostly composed of Mine 3 material (estimate: ~60%) and low-grade reprocessing material (~25%). Presence of historical material and oversize was noted in February and March reports as a factor for blend attention.'
      },
      alertas: [
        { pt: 'Material oversize acima do normal em Fevereiro', en: 'Above-normal oversize material in February' },
        { pt: 'Stock de reprocessamento a pressionar qualidade média do feed', en: 'Reprocessing stock pressuring average feed quality' },
      ],
      impactoFeed: {
        pt: 'A qualidade do stockpile pode estar a contribuir para o desvio negativo no teor de alimentação (-2.2% vs plano). A maior utilização de material low-grade reduz o teor médio do feed.',
        en: 'Stockpile quality may be contributing to negative feed grade deviation (-2.2% vs plan). Higher utilization of low-grade material reduces average feed grade.'
      }
    },
    // Bloco 4: Composição do Blend / Origem do Feed
    composicaoBlend: [
      { origem: 'Mina 3 (M3)', percentagem: 45, status: 'conforme' },
      { origem: 'Mina 4 (M4)', percentagem: 20, status: 'conforme' },
      { origem: 'Calonda', percentagem: 12, status: 'acompanhamento' },
      { origem: 'Stockpile Acumulado', percentagem: 17, status: 'pressao' },
      { origem: 'Reprocessamento / Low-grade', percentagem: 6, status: 'pressao' },
    ],
    // Bloco 5: Ponte Operação → Leitura Financeira (qualitativa, SEM USD)
    ponteFinanceira: [
      {
        facto: { pt: 'Consumo de stockpile +9.1% vs plano', en: 'Stockpile consumption +9.1% vs plan' },
        implicacaoOperacional: { pt: 'Protege throughput e continuidade no curto prazo', en: 'Protects throughput and continuity in short term' },
        leituraFinanceira: { pt: 'Protege caixa no curto prazo — mas consome reserva operacional', en: 'Protects cash in short term — but consumes operational reserve' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Peso do stock no blend 16.7% vs 14.3% planeado', en: 'Stock weight in blend 16.7% vs 14.3% planned' },
        implicacaoOperacional: { pt: 'Maior dependência de material acumulado', en: 'Higher dependency on accumulated material' },
        leituraFinanceira: { pt: 'Reduz flexibilidade futura — aumenta risco sobre qualidade', en: 'Reduces future flexibility — increases quality risk' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Stockpile de fecho -8.1% vs abertura', en: 'Closing stockpile -8.1% vs opening' },
        implicacaoOperacional: { pt: 'Menor almofada operacional disponível', en: 'Less operational buffer available' },
        leituraFinanceira: { pt: 'Menor margem de segurança — maior exposição a disrupções mineiras', en: 'Lower safety margin — higher exposure to mining disruptions' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Maior utilização de material low-grade/reprocessado', en: 'Higher utilization of low-grade/reprocessed material' },
        implicacaoOperacional: { pt: 'Pressão sobre teor médio do feed', en: 'Pressure on average feed grade' },
        leituraFinanceira: { pt: 'Possível pressão sobre quilates recuperados e qualidade comercial', en: 'Possible pressure on recovered carats and commercial quality' },
        severidade: 'high'
      },
    ],
  },
  2025: {
    period: '2025',
    keyMessages: [
      {
        iconKey: 'Package',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'ALMOFADA OPERACIONAL', en: 'OPERATIONAL BUFFER' },
        text: {
          pt: 'O stockpile é uma almofada operacional, mas o seu consumo excessivo pode mascarar fragilidade na frente mineira.',
          en: 'Stockpile is an operational buffer, but excessive consumption may mask weakness at the mining front.'
        }
      },
      {
        iconKey: 'Layers',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'VOLUME E QUALIDADE', en: 'VOLUME AND QUALITY' },
        text: {
          pt: 'O stock não deve ser lido apenas em volume, mas também em qualidade e composição do feed que alimenta a planta.',
          en: 'Stock should not be read only in volume, but also in quality and composition of the feed that supplies the plant.'
        }
      },
      {
        iconKey: 'TrendingUp',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'GESTÃO DISCIPLINADA', en: 'DISCIPLINED MANAGEMENT' },
        text: {
          pt: '2025 caracterizou-se por gestão disciplinada do stockpile, com níveis estáveis e dependência controlada.',
          en: '2025 was characterized by disciplined stockpile management, with stable levels and controlled dependency.'
        }
      }
    ],
    kpis: {
      stockpile: { actual: 310000, plan: 305000, variance: 1.6, yoyVariance: 5.1, unit: 'm³', trend: 'stable' },
      alimentacaoStockpile: { actual: 620000, plan: 610000, variance: 1.6, yoyVariance: 3.2, unit: 'm³', trend: 'stable' },
      alimentacaoNovoStock: { actual: 3900000, plan: 3850000, variance: 1.3, yoyVariance: 4.5, unit: 'm³', trend: 'stable' },
      pesoStockpileBlend: { actual: 13.7, plan: 13.7, variance: 0.0, yoyVariance: -1.2, unit: '%', trend: 'stable' },
    },
    posicaoEvolucao: {
      abertura: { value: 295000, unit: 'm³' },
      fecho: { value: 310000, unit: 'm³' },
      variacao: { value: 15000, percentagem: 5.1, unit: 'm³' },
    },
    consumoDependencia: {
      alimentacaoStockpile: { real: 620, orc: 610, variance: 1.6, unit: 'K m³' },
      alimentacaoNovoStock: { real: 3900, orc: 3850, variance: 1.3, unit: 'K m³' },
      pesoNoBlend: { real: 13.7, orc: 13.7, variance: 0.0, unit: '%' },
      leituraExecutiva: {
        pt: 'O ano de 2025 foi caracterizado por equilíbrio entre consumo de stockpile e extracção de minério novo. O peso do stockpile no blend manteve-se estável em 13.7%, conforme planeado.',
        en: '2025 was characterized by balance between stockpile consumption and new ore extraction. Stockpile weight in blend remained stable at 13.7%, as planned.'
      }
    },
    ponteFinanceira: [
      {
        facto: { pt: 'Stockpile de fecho +5.1% vs abertura', en: 'Closing stockpile +5.1% vs opening' },
        implicacaoOperacional: { pt: 'Construção de reserva operacional', en: 'Building of operational reserve' },
        leituraFinanceira: { pt: 'Aumento da almofada de segurança — protege continuidade futura', en: 'Increased safety buffer — protects future continuity' },
        severidade: 'low'
      },
      {
        facto: { pt: 'Peso do stock no blend conforme plano (13.7%)', en: 'Stock weight in blend as per plan (13.7%)' },
        implicacaoOperacional: { pt: 'Dependência controlada de material acumulado', en: 'Controlled dependency on accumulated material' },
        leituraFinanceira: { pt: 'Gestão disciplinada — sem pressão adicional sobre qualidade', en: 'Disciplined management — no additional pressure on quality' },
        severidade: 'low'
      },
    ],
  },
};

// ============================================================================
// PLANT RECOVERY DATA
// ============================================================================
export const plantRecoveryTrajectoryKpis = {
  2026: [
    {
      key: 'minerioTratado',
      label: { pt: 'Minério Tratado', en: 'Ore Processed' },
      unit: 'K m³',
      rollingData: [
        { month: 'Abr 25', value: 380 }, { month: 'Mai 25', value: 395 }, { month: 'Jun 25', value: 410 },
        { month: 'Jul 25', value: 365 }, { month: 'Ago 25', value: 385 }, { month: 'Set 25', value: 420 },
        { month: 'Out 25', value: 375 }, { month: 'Nov 25', value: 390 }, { month: 'Dez 25', value: 350 },
        { month: 'Jan 26', value: 350 }, { month: 'Fev 26', value: 360 }, { month: 'Mar 26', value: 370 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 400 }, { quarter: '3T26', value: 395 }, { quarter: '4T26', value: 380 }],
      q1Actual: 360,
      trend: 'stable',
    },
    {
      key: 'teorAlimentacao',
      label: { pt: 'Teor Alimentação', en: 'Feed Grade' },
      unit: 'cpht',
      rollingData: [
        { month: 'Abr 25', value: 15.9 }, { month: 'Mai 25', value: 16.1 }, { month: 'Jun 25', value: 15.8 },
        { month: 'Jul 25', value: 15.6 }, { month: 'Ago 25', value: 15.9 }, { month: 'Set 25', value: 16.2 },
        { month: 'Out 25', value: 15.7 }, { month: 'Nov 25', value: 15.8 }, { month: 'Dez 25', value: 15.5 },
        { month: 'Jan 26', value: 15.6 }, { month: 'Fev 26', value: 15.8 }, { month: 'Mar 26', value: 15.74 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 16.0 }, { quarter: '3T26', value: 15.9 }, { quarter: '4T26', value: 15.8 }],
      q1Actual: 15.71,
      trend: 'stable',
    },
    {
      key: 'recuperacao',
      label: { pt: 'Recuperação', en: 'Recovery' },
      unit: '%',
      rollingData: [
        { month: 'Abr 25', value: 94.2 }, { month: 'Mai 25', value: 94.5 }, { month: 'Jun 25', value: 94.1 },
        { month: 'Jul 25', value: 93.8 }, { month: 'Ago 25', value: 94.3 }, { month: 'Set 25', value: 94.8 },
        { month: 'Out 25', value: 94.0 }, { month: 'Nov 25', value: 94.4 }, { month: 'Dez 25', value: 93.5 },
        { month: 'Jan 26', value: 93.8 }, { month: 'Fev 26', value: 94.2 }, { month: 'Mar 26', value: 94.0 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 94.5 }, { quarter: '3T26', value: 94.5 }, { quarter: '4T26', value: 94.0 }],
      q1Actual: 94.0,
      trend: 'stable',
    },
  ],
  2025: [
    {
      key: 'minerioTratado',
      label: { pt: 'Minério Tratado', en: 'Ore Processed' },
      unit: 'K m³',
      rollingData: [
        { month: 'Jan', value: 345 }, { month: 'Fev', value: 350 }, { month: 'Mar', value: 355 },
        { month: 'Abr', value: 370 }, { month: 'Mai', value: 378 }, { month: 'Jun', value: 372 },
        { month: 'Jul', value: 385 }, { month: 'Ago', value: 398 }, { month: 'Set', value: 397 },
        { month: 'Out', value: 362 }, { month: 'Nov', value: 370 }, { month: 'Dez', value: 368 },
      ],
      priorYearData: [
        { month: 'Jan', value: 320 }, { month: 'Fev', value: 325 }, { month: 'Mar', value: 332 },
        { month: 'Abr', value: 345 }, { month: 'Mai', value: 352 }, { month: 'Jun', value: 348 },
        { month: 'Jul', value: 360 }, { month: 'Ago', value: 375 }, { month: 'Set', value: 372 },
        { month: 'Out', value: 340 }, { month: 'Nov', value: 348 }, { month: 'Dez', value: 345 },
      ],
      trend: 'up',
    },
    {
      key: 'teorAlimentacao',
      label: { pt: 'Teor Alimentação', en: 'Feed Grade' },
      unit: 'cpht',
      rollingData: [
        { month: 'Jan', value: 15.5 }, { month: 'Fev', value: 15.7 }, { month: 'Mar', value: 15.9 },
        { month: 'Abr', value: 15.9 }, { month: 'Mai', value: 16.1 }, { month: 'Jun', value: 15.8 },
        { month: 'Jul', value: 15.6 }, { month: 'Ago', value: 15.9 }, { month: 'Set', value: 16.2 },
        { month: 'Out', value: 15.7 }, { month: 'Nov', value: 15.8 }, { month: 'Dez', value: 15.5 },
      ],
      priorYearData: [
        { month: 'Jan', value: 15.2 }, { month: 'Fev', value: 15.4 }, { month: 'Mar', value: 15.5 },
        { month: 'Abr', value: 15.5 }, { month: 'Mai', value: 15.8 }, { month: 'Jun', value: 15.5 },
        { month: 'Jul', value: 15.3 }, { month: 'Ago', value: 15.6 }, { month: 'Set', value: 15.9 },
        { month: 'Out', value: 15.4 }, { month: 'Nov', value: 15.5 }, { month: 'Dez', value: 15.3 },
      ],
      trend: 'stable',
    },
    {
      key: 'recuperacao',
      label: { pt: 'Recuperação', en: 'Recovery' },
      unit: '%',
      rollingData: [
        { month: 'Jan', value: 93.5 }, { month: 'Fev', value: 93.8 }, { month: 'Mar', value: 94.0 },
        { month: 'Abr', value: 94.2 }, { month: 'Mai', value: 94.5 }, { month: 'Jun', value: 94.1 },
        { month: 'Jul', value: 93.8 }, { month: 'Ago', value: 94.3 }, { month: 'Set', value: 94.8 },
        { month: 'Out', value: 94.0 }, { month: 'Nov', value: 94.4 }, { month: 'Dez', value: 93.5 },
      ],
      priorYearData: [
        { month: 'Jan', value: 92.8 }, { month: 'Fev', value: 93.0 }, { month: 'Mar', value: 93.2 },
        { month: 'Abr', value: 93.5 }, { month: 'Mai', value: 93.8 }, { month: 'Jun', value: 93.4 },
        { month: 'Jul', value: 93.1 }, { month: 'Ago', value: 93.6 }, { month: 'Set', value: 94.0 },
        { month: 'Out', value: 93.3 }, { month: 'Nov', value: 93.7 }, { month: 'Dez', value: 92.9 },
      ],
      trend: 'up',
    },
  ],
};

export const plantRecoveryData = {
  2026: {
    period: '1T 2026',
    keyMessages: [
      {
        iconKey: 'Factory',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'CONVERSÃO', en: 'CONVERSION' },
        text: {
          pt: 'A planta é o ponto de conversão entre volume físico e receita potencial — throughput sem qualidade não protege margem.',
          en: 'The plant is the conversion point between physical volume and potential revenue — throughput without quality does not protect margin.'
        }
      },
      {
        iconKey: 'Gem',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'QUALIDADE', en: 'QUALITY' },
        text: {
          pt: 'O teor e o tamanho médio da pedra determinam a qualidade comercial do output — o CFO deve monitorizar não só quilates, mas também valor por quilate.',
          en: 'Grade and average stone size determine commercial quality of output — the CFO must monitor not only carats, but also value per carat.'
        }
      },
      {
        iconKey: 'AlertTriangle',
        iconBg: 'bg-amber-500/20',
        iconColor: 'text-amber-400',
        titleColor: 'text-amber-400',
        title: { pt: 'RISCO 2026', en: '2026 RISK' },
        text: {
          pt: 'O principal risco de 2026 está na estabilidade do arranque da nova lavaria e na articulação com os circuitos existentes.',
          en: 'The main 2026 risk is the stability of the new wash plant startup and articulation with existing circuits.'
        }
      }
    ],
    kpis: {
      minerioTratado: { actual: 1080000, plan: 1150000, variance: -6.1, yoyVariance: 6.9, unit: 'm³', trend: 'down' },
      teorAlimentacao: { actual: 15.74, plan: 16.10, variance: -2.2, yoyVariance: -0.5, unit: 'cpht', trend: 'down' },
      tamanhoMedioPedra: { actual: 0.39, plan: 0.44, variance: -11.4, yoyVariance: -5.0, unit: 'ct/pedra', trend: 'down' },
      pedrasEspeciais: { actual: 28, plan: 35, variance: -20.0, yoyVariance: 17.0, unit: 'un', trend: 'mixed' },
    },
    throughput: {
      monthly: [
        { month: 'Jan', tratadoReal: 350, tratadoOrc: 380, dmsReal: 210, dmsOrc: 230, quilatesReal: 5500, quilatesOrc: 6100 },
        { month: 'Fev', tratadoReal: 360, tratadoOrc: 385, dmsReal: 220, dmsOrc: 235, quilatesReal: 5700, quilatesOrc: 6200 },
        { month: 'Mar', tratadoReal: 370, tratadoOrc: 385, dmsReal: 225, dmsOrc: 235, quilatesReal: 5800, quilatesOrc: 6200 },
      ],
      ytd: {
        totalPlanta: { real: 1080, orc: 1150, variance: -6.1, unit: 'K m³' },
        dmsAlimentacao: { real: 655, orc: 700, variance: -6.4, unit: 'K m³' },
        quilates: { real: 17000, orc: 18500, variance: -8.1, unit: 'cts' },
      },
      romSummary: {
        preTratamento: { real: 425, orc: 450, unit: 'K m³' },
        lavariaCentral: { real: 655, orc: 700, unit: 'K m³' },
      }
    },
    qualidade: {
      teor: { actual: 15.74, plan: 16.10, variance: -2.2 },
      tamanhoMedio: { actual: 0.39, plan: 0.44, variance: -11.4 },
      especiais: { actual: 28, plan: 35, variance: -20.0 },
      sfdReading: {
        pt: 'Distribuição de tamanhos dentro do esperado para o blend processado. Concentração de pedras na faixa 0.3-0.5ct.',
        en: 'Size distribution within expected range for processed blend. Concentration of stones in 0.3-0.5ct range.'
      }
    },
    feedMix: {
      origens: [
        { nome: 'Mina 3 (M3)', percentagem: 45, status: 'conforme' },
        { nome: 'Mina 4 (M4)', percentagem: 30, status: 'conforme' },
        { nome: 'Calonda', percentagem: 15, status: 'acompanhamento' },
        { nome: 'Low-grade / Reprocessamento', percentagem: 10, status: 'pressao' },
      ],
      blendReading: {
        pt: 'O blend do 1T foi condicionado pela menor disponibilidade de minério de Mina 3, forçando maior utilização de material de Calonda e reprocessamento. Esta alteração no mix explica parte do desvio no teor de alimentação.',
        en: 'Q1 blend was conditioned by lower ore availability from Mine 3, forcing higher utilization of Calonda material and reprocessing. This mix change explains part of the feed grade deviation.'
      },
      observations: {
        pt: 'Registo de oversize acima do normal em Fevereiro. Reconciliação metalúrgica dentro dos parâmetros.',
        en: 'Above-normal oversize recorded in February. Metallurgical reconciliation within parameters.'
      }
    },
    constrangimentos: [
      {
        causa: { pt: 'Arranque da nova lavaria com ajustes', en: 'New wash plant startup with adjustments' },
        efeito: { pt: 'Throughput abaixo do plano nas primeiras 4 semanas', en: 'Throughput below plan in first 4 weeks' },
        severidade: 'medium'
      },
      {
        causa: { pt: 'Variabilidade no feed de Calonda', en: 'Variability in Calonda feed' },
        efeito: { pt: 'Flutuação no teor de alimentação', en: 'Fluctuation in feed grade' },
        severidade: 'medium'
      },
      {
        causa: { pt: 'Manutenção correctiva no circuito DMS', en: 'Corrective maintenance on DMS circuit' },
        efeito: { pt: 'Perda de 48h de operação em Março', en: '48h operation loss in March' },
        severidade: 'low'
      },
    ],
    ponteFinanceira: [
      {
        desvio: { pt: 'Minério tratado -6.1% vs orçamento', en: 'Ore processed -6.1% vs budget' },
        implicacaoOperacional: { pt: 'Menor conversão de minério/stock em output', en: 'Lower conversion of ore/stock to output' },
        leituraFinanceira: { pt: 'Pressão moderada sobre receita — menor volume disponível para venda', en: 'Moderate pressure on revenue — lower volume available for sale' },
        severidade: 'medium'
      },
      {
        desvio: { pt: 'Teor de alimentação -2.2% vs plano', en: 'Feed grade -2.2% vs plan' },
        implicacaoOperacional: { pt: 'Menos quilates por m³ tratado', en: 'Fewer carats per m³ processed' },
        leituraFinanceira: { pt: 'Pressão limitada — desvio dentro da tolerância operacional', en: 'Limited pressure — deviation within operational tolerance' },
        severidade: 'low'
      },
      {
        desvio: { pt: 'Tamanho médio da pedra -11.4% vs esperado', en: 'Average stone size -11.4% vs expected' },
        implicacaoOperacional: { pt: 'Concentração em pedras menores', en: 'Concentration in smaller stones' },
        leituraFinanceira: { pt: 'Possível pressão na qualidade comercial / preço médio', en: 'Possible pressure on commercial quality / average price' },
        severidade: 'medium'
      },
      {
        desvio: { pt: 'N.º de especiais -20% vs plano', en: 'Special stones -20% vs plan' },
        implicacaoOperacional: { pt: 'Menor ocorrência de pedras de alto valor', en: 'Lower occurrence of high-value stones' },
        leituraFinanceira: { pt: 'Menor upside comercial — risco sobre receita premium', en: 'Lower commercial upside — risk on premium revenue' },
        severidade: 'high'
      },
    ],
  },
  2025: {
    period: '2025',
    keyMessages: [
      {
        iconKey: 'Factory',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'CONVERSÃO', en: 'CONVERSION' },
        text: {
          pt: 'A planta é o ponto de conversão entre volume físico e receita potencial — throughput sem qualidade não protege margem.',
          en: 'The plant is the conversion point between physical volume and potential revenue — throughput without quality does not protect margin.'
        }
      },
      {
        iconKey: 'Gem',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'QUALIDADE', en: 'QUALITY' },
        text: {
          pt: 'O teor e o tamanho médio da pedra determinam a qualidade comercial do output — o CFO deve monitorizar não só quilates, mas também valor por quilate.',
          en: 'Grade and average stone size determine commercial quality of output — the CFO must monitor not only carats, but also value per carat.'
        }
      },
      {
        iconKey: 'TrendingUp',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'RESULTADO 2025', en: '2025 RESULT' },
        text: {
          pt: '2025 foi um ano de performance sólida da planta, com teor acima do plano e recuperação de pedras especiais no 2º semestre.',
          en: '2025 was a year of solid plant performance, with grade above plan and special stones recovery in H2.'
        }
      }
    ],
    kpis: {
      minerioTratado: { actual: 1100000, plan: 1150000, variance: -4.3, yoyVariance: 4.8, unit: 'm³', trend: 'stable' },
      teorAlimentacao: { actual: 15.82, plan: 15.65, variance: 1.1, yoyVariance: 2.3, unit: 'cpht', trend: 'up' },
      tamanhoMedioPedra: { actual: 0.41, plan: 0.42, variance: -2.4, yoyVariance: 3.1, unit: 'ct/pedra', trend: 'stable' },
      pedrasEspeciais: { actual: 24, plan: 22, variance: 9.1, yoyVariance: 20.0, unit: 'un', trend: 'up' },
    },
    throughput: {
      ytd: {
        totalPlanta: { real: 1100, orc: 1150, variance: -4.3, unit: 'K m³' },
        dmsAlimentacao: { real: 680, orc: 700, variance: -2.9, unit: 'K m³' },
        quilates: { real: 17400, orc: 18000, variance: -3.3, unit: 'cts' },
      },
    },
    qualidade: {
      teor: { actual: 15.82, plan: 15.65, variance: 1.1 },
      tamanhoMedio: { actual: 0.41, plan: 0.42, variance: -2.4 },
      especiais: { actual: 24, plan: 22, variance: 9.1 },
    },
    ponteFinanceira: [
      {
        desvio: { pt: 'Minério tratado -4.3% vs orçamento', en: 'Ore processed -4.3% vs budget' },
        implicacaoOperacional: { pt: 'Volume ligeiramente abaixo do plano', en: 'Volume slightly below plan' },
        leituraFinanceira: { pt: 'Pressão limitada — compensada pelo teor favorável', en: 'Limited pressure — offset by favorable grade' },
        severidade: 'low'
      },
      {
        desvio: { pt: 'Teor de alimentação +1.1% vs plano', en: 'Feed grade +1.1% vs plan' },
        implicacaoOperacional: { pt: 'Mais quilates por m³ tratado', en: 'More carats per m³ processed' },
        leituraFinanceira: { pt: 'Contribuição positiva para receita', en: 'Positive contribution to revenue' },
        severidade: 'low'
      },
    ],
  },
};

// ============================================================================
// PRODUCTION REVENUE DATA
// ============================================================================
export const productionRevenueTrajectoryKpis = {
  2026: [
    {
      key: 'quilatesRecuperados',
      label: { pt: 'Quilates Recuperados', en: 'Carats Recovered' },
      unit: 'Kcts',
      rollingData: [
        { month: 'Abr 25', value: 5.8 }, { month: 'Mai 25', value: 5.95 }, { month: 'Jun 25', value: 6.1 },
        { month: 'Jul 25', value: 5.7 }, { month: 'Ago 25', value: 5.85 }, { month: 'Set 25', value: 6.2 },
        { month: 'Out 25', value: 5.9 }, { month: 'Nov 25', value: 6.05 }, { month: 'Dez 25', value: 5.65 },
        { month: 'Jan 26', value: 5.5 }, { month: 'Fev 26', value: 5.7 }, { month: 'Mar 26', value: 5.8 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 6.0 }, { quarter: '3T26', value: 5.8 }, { quarter: '4T26', value: 5.6 }],
      q1Actual: 5.67,
      trend: 'stable',
    },
    {
      key: 'teor',
      label: { pt: 'Teor', en: 'Grade' },
      unit: 'cpht',
      rollingData: [
        { month: 'Abr 25', value: 15.9 }, { month: 'Mai 25', value: 16.1 }, { month: 'Jun 25', value: 15.8 },
        { month: 'Jul 25', value: 15.6 }, { month: 'Ago 25', value: 15.9 }, { month: 'Set 25', value: 16.2 },
        { month: 'Out 25', value: 15.7 }, { month: 'Nov 25', value: 15.8 }, { month: 'Dez 25', value: 15.5 },
        { month: 'Jan 26', value: 15.6 }, { month: 'Fev 26', value: 15.8 }, { month: 'Mar 26', value: 15.74 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 16.0 }, { quarter: '3T26', value: 15.9 }, { quarter: '4T26', value: 15.8 }],
      q1Actual: 15.71,
      trend: 'stable',
    },
    {
      key: 'tamanhoMedio',
      label: { pt: 'Tamanho Médio', en: 'Avg Size' },
      unit: 'ct/st',
      rollingData: [
        { month: 'Abr 25', value: 0.42 }, { month: 'Mai 25', value: 0.43 }, { month: 'Jun 25', value: 0.41 },
        { month: 'Jul 25', value: 0.40 }, { month: 'Ago 25', value: 0.42 }, { month: 'Set 25', value: 0.44 },
        { month: 'Out 25', value: 0.41 }, { month: 'Nov 25', value: 0.40 }, { month: 'Dez 25', value: 0.38 },
        { month: 'Jan 26', value: 0.38 }, { month: 'Fev 26', value: 0.40 }, { month: 'Mar 26', value: 0.39 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 0.42 }, { quarter: '3T26', value: 0.41 }, { quarter: '4T26', value: 0.40 }],
      q1Actual: 0.39,
      trend: 'down',
    },
  ],
  2025: [
    {
      key: 'quilatesRecuperados',
      label: { pt: 'Quilates Recuperados', en: 'Carats Recovered' },
      unit: 'Kcts',
      rollingData: [
        { month: 'Jan', value: 5.2 }, { month: 'Fev', value: 5.4 }, { month: 'Mar', value: 5.6 },
        { month: 'Abr', value: 5.8 }, { month: 'Mai', value: 5.95 }, { month: 'Jun', value: 6.0 },
        { month: 'Jul', value: 5.7 }, { month: 'Ago', value: 5.85 }, { month: 'Set', value: 6.2 },
        { month: 'Out', value: 5.9 }, { month: 'Nov', value: 6.05 }, { month: 'Dez', value: 5.65 },
      ],
      priorYearData: [
        { month: 'Jan', value: 4.9 }, { month: 'Fev', value: 5.1 }, { month: 'Mar', value: 5.3 },
        { month: 'Abr', value: 5.5 }, { month: 'Mai', value: 5.65 }, { month: 'Jun', value: 5.7 },
        { month: 'Jul', value: 5.4 }, { month: 'Ago', value: 5.55 }, { month: 'Set', value: 5.85 },
        { month: 'Out', value: 5.6 }, { month: 'Nov', value: 5.75 }, { month: 'Dez', value: 5.4 },
      ],
      trend: 'up',
    },
    {
      key: 'teor',
      label: { pt: 'Teor', en: 'Grade' },
      unit: 'cpht',
      rollingData: [
        { month: 'Jan', value: 15.5 }, { month: 'Fev', value: 15.7 }, { month: 'Mar', value: 15.9 },
        { month: 'Abr', value: 15.9 }, { month: 'Mai', value: 16.1 }, { month: 'Jun', value: 15.8 },
        { month: 'Jul', value: 15.6 }, { month: 'Ago', value: 15.9 }, { month: 'Set', value: 16.2 },
        { month: 'Out', value: 15.7 }, { month: 'Nov', value: 15.8 }, { month: 'Dez', value: 15.5 },
      ],
      priorYearData: [
        { month: 'Jan', value: 15.2 }, { month: 'Fev', value: 15.4 }, { month: 'Mar', value: 15.5 },
        { month: 'Abr', value: 15.5 }, { month: 'Mai', value: 15.8 }, { month: 'Jun', value: 15.5 },
        { month: 'Jul', value: 15.3 }, { month: 'Ago', value: 15.6 }, { month: 'Set', value: 15.9 },
        { month: 'Out', value: 15.4 }, { month: 'Nov', value: 15.5 }, { month: 'Dez', value: 15.3 },
      ],
      trend: 'stable',
    },
    {
      key: 'tamanhoMedio',
      label: { pt: 'Tamanho Médio', en: 'Avg Size' },
      unit: 'ct/st',
      rollingData: [
        { month: 'Jan', value: 0.40 }, { month: 'Fev', value: 0.41 }, { month: 'Mar', value: 0.42 },
        { month: 'Abr', value: 0.42 }, { month: 'Mai', value: 0.43 }, { month: 'Jun', value: 0.41 },
        { month: 'Jul', value: 0.40 }, { month: 'Ago', value: 0.42 }, { month: 'Set', value: 0.44 },
        { month: 'Out', value: 0.41 }, { month: 'Nov', value: 0.40 }, { month: 'Dez', value: 0.38 },
      ],
      priorYearData: [
        { month: 'Jan', value: 0.38 }, { month: 'Fev', value: 0.39 }, { month: 'Mar', value: 0.40 },
        { month: 'Abr', value: 0.40 }, { month: 'Mai', value: 0.41 }, { month: 'Jun', value: 0.39 },
        { month: 'Jul', value: 0.38 }, { month: 'Ago', value: 0.40 }, { month: 'Set', value: 0.42 },
        { month: 'Out', value: 0.39 }, { month: 'Nov', value: 0.38 }, { month: 'Dez', value: 0.37 },
      ],
      trend: 'stable',
    },
  ],
};

export const productionRevenueData = {
  2026: {
    period: '1T 2026',
    keyMessages: [
      {
        iconKey: 'Gem',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'QUALIDADE DA PRODUÇÃO', en: 'PRODUCTION QUALITY' },
        text: {
          pt: 'A receita operacional começa na qualidade da produção, não apenas no volume tratado. Quilates sem qualidade comercial não protegem valor.',
          en: 'Operating revenue starts with production quality, not just processed volume. Carats without commercial quality do not protect value.'
        }
      },
      {
        iconKey: 'Target',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'MIX DE PRODUÇÃO', en: 'PRODUCTION MIX' },
        text: {
          pt: 'Esta página deve mostrar se a operação está a produzir o mix que sustenta receita e não apenas o volume que sustenta a planta.',
          en: 'This page should show if operations are producing the mix that sustains revenue and not just the volume that sustains the plant.'
        }
      },
      {
        iconKey: 'AlertTriangle',
        iconBg: 'bg-amber-500/20',
        iconColor: 'text-amber-400',
        titleColor: 'text-amber-400',
        title: { pt: 'DRIVERS DE RECEITA', en: 'REVENUE DRIVERS' },
        text: {
          pt: 'Quilates recuperados, teor, tamanho médio da pedra e especiais são os drivers operacionais da receita — o desvio pode estar em qualquer um deles.',
          en: 'Recovered carats, grade, average stone size and specials are the operational revenue drivers — deviation can be in any of them.'
        }
      }
    ],
    kpis: {
      quilatesRecuperados: { actual: 17000, plan: 18500, variance: -8.1, yoyVariance: -2.3, unit: 'cts', trend: 'down' },
      teor: { actual: 15.74, plan: 16.10, variance: -2.2, yoyVariance: -0.5, unit: 'cpht', trend: 'down' },
      tamanhoMedioPedra: { actual: 0.39, plan: 0.44, variance: -11.4, yoyVariance: -5.0, unit: 'ct/pedra', trend: 'down' },
      pedrasEspeciais: { actual: 28, plan: 35, variance: -20.0, yoyVariance: 17.0, unit: 'un', trend: 'mixed' },
    },
    producaoMensal: {
      monthly: [
        { month: 'Jan', quilatesReal: 5500, quilatesOrc: 6100, minerioTratado: 350 },
        { month: 'Fev', quilatesReal: 5700, quilatesOrc: 6200, minerioTratado: 360 },
        { month: 'Mar', quilatesReal: 5800, quilatesOrc: 6200, minerioTratado: 370 },
      ],
      ytd: {
        quilates: { real: 17000, orc: 18500, variance: -8.1, unit: 'cts' },
        minerioTratado: { real: 1080, orc: 1150, variance: -6.1, unit: 'K m³' },
        conversao: { real: 15.74, orc: 16.10, variance: -2.2, unit: 'cpht' },
      },
      leituraExecutiva: {
        pt: 'Os quilates recuperados ficaram 8.1% abaixo do plano, explicados pela combinação de menor minério tratado (-6.1%) e menor teor de alimentação (-2.2%). O desvio está maioritariamente no volume tratado.',
        en: 'Recovered carats were 8.1% below plan, explained by the combination of lower ore processed (-6.1%) and lower feed grade (-2.2%). The deviation is mostly in processed volume.'
      }
    },
    qualidadeProduto: {
      teor: { actual: 15.74, plan: 16.10, variance: -2.2 },
      tamanhoMedio: { actual: 0.39, plan: 0.44, variance: -11.4 },
      especiais: { actual: 28, plan: 35, variance: -20.0 },
      sfdReading: {
        pt: 'Distribuição de tamanhos concentrada na faixa 0.3-0.5ct. Menor ocorrência de pedras >1ct do que o esperado.',
        en: 'Size distribution concentrated in 0.3-0.5ct range. Lower occurrence of >1ct stones than expected.'
      },
      leituraQualidade: {
        pt: 'O tamanho médio da pedra (-11.4% vs plano) e o número de especiais (-20%) indicam pressão sobre a qualidade comercial do produto. O teor está ligeiramente abaixo do esperado (-2.2%).',
        en: 'Average stone size (-11.4% vs plan) and number of specials (-20%) indicate pressure on commercial product quality. Grade is slightly below expected (-2.2%).'
      }
    },
    mixOperacional: [
      { origem: 'Mina 3 (M3)', percentagem: 45, qualidade: 'alto', status: 'conforme' },
      { origem: 'Mina 4 (M4)', percentagem: 20, qualidade: 'médio', status: 'conforme' },
      { origem: 'Calonda', percentagem: 12, qualidade: 'médio', status: 'acompanhamento' },
      { origem: 'Stockpile Acumulado', percentagem: 17, qualidade: 'variável', status: 'pressao' },
      { origem: 'Reprocessamento / Low-grade', percentagem: 6, qualidade: 'baixo', status: 'pressao' },
    ],
    mixReading: {
      pt: 'O blend do período teve maior peso de stockpile acumulado (17%) e material de reprocessamento (6%) do que o planeado. Esta alteração no mix ajuda a explicar a pressão sobre o teor e o tamanho médio da pedra.',
      en: 'Period blend had higher weight of accumulated stockpile (17%) and reprocessing material (6%) than planned. This mix change helps explain pressure on grade and average stone size.'
    },
    constrangimentos: [
      {
        facto: { pt: 'Menor extracção de minério novo (-8.6% vs plano)', en: 'Lower new ore extraction (-8.6% vs plan)' },
        efeito: { pt: 'Maior dependência de stockpile para alimentar a planta', en: 'Higher stockpile dependency to feed plant' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Variabilidade no feed de Calonda', en: 'Variability in Calonda feed' },
        efeito: { pt: 'Flutuação no teor de alimentação', en: 'Fluctuation in feed grade' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Material oversize acima do normal', en: 'Above-normal oversize material' },
        efeito: { pt: 'Menor recuperação de pedras de maior dimensão', en: 'Lower recovery of larger stones' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Arranque da nova lavaria com ajustes', en: 'New wash plant startup with adjustments' },
        efeito: { pt: 'Throughput abaixo do plano nas primeiras semanas', en: 'Throughput below plan in first weeks' },
        severidade: 'low'
      },
    ],
    ponteFinanceira: [
      {
        facto: { pt: 'Quilates recuperados -8.1% vs plano', en: 'Recovered carats -8.1% vs plan' },
        implicacaoOperacional: { pt: 'Menor base física de produção', en: 'Lower physical production base' },
        leituraFinanceira: { pt: 'Menor base de receita — pressão sobre volume de vendas', en: 'Lower revenue base — pressure on sales volume' },
        severidade: 'high'
      },
      {
        facto: { pt: 'Teor de alimentação -2.2% vs plano', en: 'Feed grade -2.2% vs plan' },
        implicacaoOperacional: { pt: 'Menor geração de quilates por unidade tratada', en: 'Lower carat generation per unit processed' },
        leituraFinanceira: { pt: 'Pressão limitada — desvio dentro da tolerância operacional', en: 'Limited pressure — deviation within operational tolerance' },
        severidade: 'low'
      },
      {
        facto: { pt: 'Tamanho médio da pedra -11.4% vs esperado', en: 'Average stone size -11.4% vs expected' },
        implicacaoOperacional: { pt: 'Concentração de produção em pedras menores', en: 'Production concentration in smaller stones' },
        leituraFinanceira: { pt: 'Possível pressão sobre qualidade comercial / preço médio', en: 'Possible pressure on commercial quality / average price' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'N.º de especiais -20% vs plano', en: 'Special stones -20% vs plan' },
        implicacaoOperacional: { pt: 'Menor ocorrência de pedras de alto valor', en: 'Lower occurrence of high-value stones' },
        leituraFinanceira: { pt: 'Menor upside comercial — risco sobre receita premium', en: 'Lower commercial upside — risk on premium revenue' },
        severidade: 'high'
      },
    ],
  },
  2025: {
    period: '2025',
    keyMessages: [
      {
        iconKey: 'Gem',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'QUALIDADE DA PRODUÇÃO', en: 'PRODUCTION QUALITY' },
        text: {
          pt: 'A receita operacional começa na qualidade da produção, não apenas no volume tratado. Quilates sem qualidade comercial não protegem valor.',
          en: 'Operating revenue starts with production quality, not just processed volume. Carats without commercial quality do not protect value.'
        }
      },
      {
        iconKey: 'Target',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'MIX DE PRODUÇÃO', en: 'PRODUCTION MIX' },
        text: {
          pt: 'Esta página deve mostrar se a operação está a produzir o mix que sustenta receita e não apenas o volume que sustenta a planta.',
          en: 'This page should show if operations are producing the mix that sustains revenue and not just the volume that sustains the plant.'
        }
      },
      {
        iconKey: 'TrendingUp',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'RESULTADO 2025', en: '2025 RESULT' },
        text: {
          pt: '2025 foi um ano de produção consistente, com qualidade do produto alinhada ao plano e ocorrência de especiais acima do esperado.',
          en: '2025 was a year of consistent production, with product quality aligned to plan and special stones above expected.'
        }
      }
    ],
    kpis: {
      quilatesRecuperados: { actual: 69600, plan: 72000, variance: -3.3, yoyVariance: 4.5, unit: 'cts', trend: 'stable' },
      teor: { actual: 15.82, plan: 15.65, variance: 1.1, yoyVariance: 2.3, unit: 'cpht', trend: 'up' },
      tamanhoMedioPedra: { actual: 0.41, plan: 0.42, variance: -2.4, yoyVariance: 3.1, unit: 'ct/pedra', trend: 'stable' },
      pedrasEspeciais: { actual: 96, plan: 88, variance: 9.1, yoyVariance: 20.0, unit: 'un', trend: 'up' },
    },
    producaoMensal: {
      ytd: {
        quilates: { real: 69600, orc: 72000, variance: -3.3, unit: 'cts' },
        minerioTratado: { real: 4400, orc: 4600, variance: -4.3, unit: 'K m³' },
        conversao: { real: 15.82, orc: 15.65, variance: 1.1, unit: 'cpht' },
      },
      leituraExecutiva: {
        pt: 'O ano de 2025 foi caracterizado por produção consistente e qualidade do produto alinhada ou acima do plano. O teor favorável (+1.1%) e o número de especiais acima do esperado (+9.1%) compensaram parcialmente o menor volume tratado.',
        en: '2025 was characterized by consistent production and product quality aligned or above plan. Favorable grade (+1.1%) and special stones above expected (+9.1%) partially offset lower processed volume.'
      }
    },
    qualidadeProduto: {
      teor: { actual: 15.82, plan: 15.65, variance: 1.1 },
      tamanhoMedio: { actual: 0.41, plan: 0.42, variance: -2.4 },
      especiais: { actual: 96, plan: 88, variance: 9.1 },
    },
    ponteFinanceira: [
      {
        facto: { pt: 'Quilates recuperados -3.3% vs plano', en: 'Recovered carats -3.3% vs plan' },
        implicacaoOperacional: { pt: 'Volume ligeiramente abaixo do plano', en: 'Volume slightly below plan' },
        leituraFinanceira: { pt: 'Pressão limitada — parcialmente compensada pela qualidade', en: 'Limited pressure — partially offset by quality' },
        severidade: 'low'
      },
      {
        facto: { pt: 'Teor de alimentação +1.1% vs plano', en: 'Feed grade +1.1% vs plan' },
        implicacaoOperacional: { pt: 'Maior geração de quilates por unidade tratada', en: 'Higher carat generation per unit processed' },
        leituraFinanceira: { pt: 'Contribuição positiva para a base de receita', en: 'Positive contribution to revenue base' },
        severidade: 'low'
      },
      {
        facto: { pt: 'N.º de especiais +9.1% vs plano', en: 'Special stones +9.1% vs plan' },
        implicacaoOperacional: { pt: 'Maior ocorrência de pedras de alto valor', en: 'Higher occurrence of high-value stones' },
        leituraFinanceira: { pt: 'Potencial upside comercial — contribuição para receita premium', en: 'Potential commercial upside — contribution to premium revenue' },
        severidade: 'low'
      },
    ],
  },
};

// ============================================================================
// OPERATIONAL EFFICIENCY DATA
// ============================================================================
export const efficiencyTrajectoryKpis = {
  2026: [
    {
      key: 'disponibilidade',
      label: { pt: 'Disponibilidade Planta', en: 'Plant Availability' },
      unit: '%',
      rollingData: [
        { month: 'Abr 25', value: 92.5 }, { month: 'Mai 25', value: 93.2 }, { month: 'Jun 25', value: 91.8 },
        { month: 'Jul 25', value: 90.5 }, { month: 'Ago 25', value: 92.1 }, { month: 'Set 25', value: 93.8 },
        { month: 'Out 25', value: 91.2 }, { month: 'Nov 25', value: 92.6 }, { month: 'Dez 25', value: 89.5 },
        { month: 'Jan 26', value: 92.0 }, { month: 'Fev 26', value: 93.1 }, { month: 'Mar 26', value: 92.3 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 94.5 }, { quarter: '3T26', value: 95.0 }, { quarter: '4T26', value: 94.0 }],
      q1Actual: 92.3,
      trend: 'stable',
    },
    {
      key: 'utilizacao',
      label: { pt: 'Utilização Efectiva', en: 'Effective Utilization' },
      unit: '%',
      rollingData: [
        { month: 'Abr 25', value: 84.2 }, { month: 'Mai 25', value: 85.5 }, { month: 'Jun 25', value: 83.8 },
        { month: 'Jul 25', value: 81.2 }, { month: 'Ago 25', value: 84.0 }, { month: 'Set 25', value: 86.2 },
        { month: 'Out 25', value: 82.5 }, { month: 'Nov 25', value: 84.8 }, { month: 'Dez 25', value: 79.5 },
        { month: 'Jan 26', value: 83.5 }, { month: 'Fev 26', value: 84.2 }, { month: 'Mar 26', value: 82.8 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 86.0 }, { quarter: '3T26', value: 87.0 }, { quarter: '4T26', value: 85.5 }],
      q1Actual: 83.5,
      trend: 'stable',
    },
    {
      key: 'horasParagem',
      label: { pt: 'Horas Paragem', en: 'Downtime Hours' },
      unit: 'hrs',
      rollingData: [
        { month: 'Abr 25', value: 135 }, { month: 'Mai 25', value: 128 }, { month: 'Jun 25', value: 145 },
        { month: 'Jul 25', value: 168 }, { month: 'Ago 25', value: 142 }, { month: 'Set 25', value: 118 },
        { month: 'Out 25', value: 155 }, { month: 'Nov 25', value: 138 }, { month: 'Dez 25', value: 185 },
        { month: 'Jan 26', value: 145 }, { month: 'Fev 26', value: 130 }, { month: 'Mar 26', value: 142 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 120 }, { quarter: '3T26', value: 115 }, { quarter: '4T26', value: 125 }],
      q1Actual: 139,
      trend: 'stable',
    },
    {
      key: 'taxaTransferenciaDMS',
      label: { pt: 'Taxa Transf. DMS', en: 'DMS Transfer Rate' },
      unit: 'm³/h',
      rollingData: [
        { month: 'Abr 25', value: 142 }, { month: 'Mai 25', value: 145 }, { month: 'Jun 25', value: 140 },
        { month: 'Jul 25', value: 138 }, { month: 'Ago 25', value: 143 }, { month: 'Set 25', value: 147 },
        { month: 'Out 25', value: 141 }, { month: 'Nov 25', value: 144 }, { month: 'Dez 25', value: 136 },
        { month: 'Jan 26', value: 143 }, { month: 'Fev 26', value: 145 }, { month: 'Mar 26', value: 145 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 150 }, { quarter: '3T26', value: 152 }, { quarter: '4T26', value: 148 }],
      q1Actual: 144.3,
      trend: 'up',
    },
  ],
  2025: [
    {
      key: 'disponibilidade',
      label: { pt: 'Disponibilidade Planta', en: 'Plant Availability' },
      unit: '%',
      rollingData: [
        { month: 'Jan', value: 90.2 }, { month: 'Fev', value: 91.5 }, { month: 'Mar', value: 91.8 },
        { month: 'Abr', value: 92.5 }, { month: 'Mai', value: 93.2 }, { month: 'Jun', value: 93.8 },
        { month: 'Jul', value: 92.8 }, { month: 'Ago', value: 94.2 }, { month: 'Set', value: 94.5 },
        { month: 'Out', value: 93.5 }, { month: 'Nov', value: 94.0 }, { month: 'Dez', value: 93.8 },
      ],
      priorYearData: [
        { month: 'Jan', value: 88.5 }, { month: 'Fev', value: 89.2 }, { month: 'Mar', value: 89.8 },
        { month: 'Abr', value: 90.5 }, { month: 'Mai', value: 91.0 }, { month: 'Jun', value: 91.5 },
        { month: 'Jul', value: 90.8 }, { month: 'Ago', value: 92.0 }, { month: 'Set', value: 92.5 },
        { month: 'Out', value: 91.5 }, { month: 'Nov', value: 92.0 }, { month: 'Dez', value: 91.8 },
      ],
      trend: 'up',
    },
    {
      key: 'utilizacao',
      label: { pt: 'Utilização Efectiva', en: 'Effective Utilization' },
      unit: '%',
      rollingData: [
        { month: 'Jan', value: 82.0 }, { month: 'Fev', value: 83.2 }, { month: 'Mar', value: 83.8 },
        { month: 'Abr', value: 84.2 }, { month: 'Mai', value: 85.5 }, { month: 'Jun', value: 83.8 },
        { month: 'Jul', value: 81.2 }, { month: 'Ago', value: 84.0 }, { month: 'Set', value: 86.2 },
        { month: 'Out', value: 82.5 }, { month: 'Nov', value: 84.8 }, { month: 'Dez', value: 79.5 },
      ],
      priorYearData: [
        { month: 'Jan', value: 80.0 }, { month: 'Fev', value: 81.0 }, { month: 'Mar', value: 81.5 },
        { month: 'Abr', value: 82.0 }, { month: 'Mai', value: 83.0 }, { month: 'Jun', value: 81.5 },
        { month: 'Jul', value: 79.5 }, { month: 'Ago', value: 82.0 }, { month: 'Set', value: 84.0 },
        { month: 'Out', value: 80.5 }, { month: 'Nov', value: 82.5 }, { month: 'Dez', value: 78.0 },
      ],
      trend: 'stable',
    },
    {
      key: 'horasParagem',
      label: { pt: 'Horas Paragem', en: 'Downtime Hours' },
      unit: 'hrs',
      rollingData: [
        { month: 'Jan', value: 160 }, { month: 'Fev', value: 150 }, { month: 'Mar', value: 145 },
        { month: 'Abr', value: 135 }, { month: 'Mai', value: 128 }, { month: 'Jun', value: 145 },
        { month: 'Jul', value: 168 }, { month: 'Ago', value: 142 }, { month: 'Set', value: 118 },
        { month: 'Out', value: 155 }, { month: 'Nov', value: 138 }, { month: 'Dez', value: 185 },
      ],
      priorYearData: [
        { month: 'Jan', value: 175 }, { month: 'Fev', value: 165 }, { month: 'Mar', value: 158 },
        { month: 'Abr', value: 150 }, { month: 'Mai', value: 142 }, { month: 'Jun', value: 155 },
        { month: 'Jul', value: 180 }, { month: 'Ago', value: 158 }, { month: 'Set', value: 135 },
        { month: 'Out', value: 168 }, { month: 'Nov', value: 152 }, { month: 'Dez', value: 195 },
      ],
      trend: 'down',
    },
    {
      key: 'taxaTransferenciaDMS',
      label: { pt: 'Taxa Transf. DMS', en: 'DMS Transfer Rate' },
      unit: 'm³/h',
      rollingData: [
        { month: 'Jan', value: 138 }, { month: 'Fev', value: 140 }, { month: 'Mar', value: 141 },
        { month: 'Abr', value: 142 }, { month: 'Mai', value: 145 }, { month: 'Jun', value: 140 },
        { month: 'Jul', value: 138 }, { month: 'Ago', value: 143 }, { month: 'Set', value: 147 },
        { month: 'Out', value: 141 }, { month: 'Nov', value: 144 }, { month: 'Dez', value: 136 },
      ],
      priorYearData: [
        { month: 'Jan', value: 132 }, { month: 'Fev', value: 134 }, { month: 'Mar', value: 135 },
        { month: 'Abr', value: 136 }, { month: 'Mai', value: 138 }, { month: 'Jun', value: 135 },
        { month: 'Jul', value: 133 }, { month: 'Ago', value: 137 }, { month: 'Set', value: 140 },
        { month: 'Out', value: 136 }, { month: 'Nov', value: 138 }, { month: 'Dez', value: 132 },
      ],
      trend: 'up',
    },
  ],
};

export const efficiencyData = {
  2026: {
    period: '1T 2026',
    keyMessages: [
      {
        iconKey: 'Gauge',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'CAPACIDADE ÚTIL', en: 'USEFUL CAPACITY' },
        text: {
          pt: 'A eficiência operacional mede a capacidade dos activos de transformar capacidade instalada em produção útil. Disponibilidade sem utilização efectiva não protege o plano.',
          en: 'Operational efficiency measures the ability of assets to transform installed capacity into useful production. Availability without effective utilization does not protect the plan.'
        }
      },
      {
        iconKey: 'Clock',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'FIABILIDADE', en: 'RELIABILITY' },
        text: {
          pt: 'Esta página responde se os activos e circuitos críticos estão a entregar a fiabilidade e produtividade necessárias para suportar o plano operacional.',
          en: 'This page answers whether critical assets and circuits are delivering the reliability and productivity needed to support the operational plan.'
        }
      },
      {
        iconKey: 'AlertTriangle',
        iconBg: 'bg-amber-500/20',
        iconColor: 'text-amber-400',
        titleColor: 'text-amber-400',
        title: { pt: 'RISCO DE EXECUÇÃO', en: 'EXECUTION RISK' },
        text: {
          pt: 'Para o CFO, esta página deve mostrar se os constrangimentos operacionais estão a pressionar throughput, custo unitário, previsibilidade e risco de execução.',
          en: 'For the CFO, this page should show if operational constraints are pressuring throughput, unit cost, predictability and execution risk.'
        }
      }
    ],
    kpis: {
      disponibilidadePlanta: { actual: 92.3, plan: 95.0, variance: -2.8, yoyVariance: -1.5, unit: '%', trend: 'down' },
      utilizacaoEfetiva: { actual: 82.8, plan: 88.0, variance: -5.9, yoyVariance: -4.2, unit: '%', trend: 'down' },
      horasParagemPlanta: { actual: 142, plan: 110, variance: 29.1, yoyVariance: 18.3, unit: 'hrs', trend: 'up' },
      taxaTransferenciaDMS: { actual: 145, plan: 150, variance: -3.3, yoyVariance: -2.0, unit: 'm³/h', trend: 'down' },
    },
    disponibilidadeUtilizacao: {
      circuitos: [
        { nome: 'Pré-tratamento', disponibilidade: 94.2, utilizacao: 85.5, utilizacaoEfetiva: 80.6, status: 'conforme' },
        { nome: 'Lavaria Central', disponibilidade: 91.8, utilizacao: 82.3, utilizacaoEfetiva: 75.6, status: 'acompanhamento' },
        { nome: 'DMS Principal', disponibilidade: 93.5, utilizacao: 88.2, utilizacaoEfetiva: 82.5, status: 'conforme' },
        { nome: 'Circuito de Recuperação', disponibilidade: 89.2, utilizacao: 78.5, utilizacaoEfetiva: 70.0, status: 'pressao' },
      ],
      leituraExecutiva: {
        pt: 'A disponibilidade dos circuitos críticos está globalmente acima de 89%, mas a utilização efectiva (combinação de disponibilidade × utilização) revela que apenas 70-82% da capacidade instalada está a ser convertida em tempo produtivo. O circuito de recuperação apresenta a maior lacuna.',
        en: 'Critical circuit availability is globally above 89%, but effective utilization (availability × utilization) reveals that only 70-82% of installed capacity is being converted to productive time. The recovery circuit shows the largest gap.'
      }
    },
    paragensTempoPerdido: {
      monthly: [
        { month: 'Jan', paragensPlaneadas: 45, paragensNaoPlaneadas: 100, total: 145 },
        { month: 'Fev', paragensPlaneadas: 42, paragensNaoPlaneadas: 88, total: 130 },
        { month: 'Mar', paragensPlaneadas: 48, paragensNaoPlaneadas: 94, total: 142 },
      ],
      ytd: {
        total: { real: 417, orc: 330, variance: 26.4, unit: 'hrs' },
        planeadas: { real: 135, orc: 130, variance: 3.8, unit: 'hrs' },
        naoPlaneadas: { real: 282, orc: 200, variance: 41.0, unit: 'hrs' },
      },
      principaisCausas: [
        { causa: { pt: 'Manutenção correctiva britador primário', en: 'Primary crusher corrective maintenance' }, horas: 85, percentagem: 30.1 },
        { causa: { pt: 'Limpeza e desobstrução de crivos', en: 'Screen cleaning and unblocking' }, horas: 62, percentagem: 22.0 },
        { causa: { pt: 'Falha de bombas de alimentação', en: 'Feed pump failure' }, horas: 48, percentagem: 17.0 },
        { causa: { pt: 'Substituição de correias transportadoras', en: 'Conveyor belt replacement' }, horas: 42, percentagem: 14.9 },
        { causa: { pt: 'Outros (combustível, checklists, arranques)', en: 'Others (fuel, checklists, startups)' }, horas: 45, percentagem: 16.0 },
      ],
      leituraExecutiva: {
        pt: 'O 1T 2026 acumulou 417 horas de paragem (+26% vs plano), com 68% do tempo perdido em paragens não planeadas. O britador primário e os crivos concentram mais de 50% das horas de interrupção.',
        en: 'Q1 2026 accumulated 417 downtime hours (+26% vs plan), with 68% of lost time in unplanned shutdowns. Primary crusher and screens concentrate more than 50% of interruption hours.'
      }
    },
    utilizacaoEfetivaCircuito: {
      monthly: [
        { month: 'Jan', utilizReal: 80.5, utilizOrc: 88.0 },
        { month: 'Fev', utilizReal: 84.2, utilizOrc: 88.0 },
        { month: 'Mar', utilizReal: 83.7, utilizOrc: 88.0 },
      ],
      ytd: { real: 82.8, orc: 88.0, variance: -5.9, unit: '%' },
    },
    taxaTransferenciaDMSData: {
      monthly: [
        { month: 'Jan', taxaReal: 142, taxaOrc: 150 },
        { month: 'Fev', taxaReal: 148, taxaOrc: 150 },
        { month: 'Mar', taxaReal: 145, taxaOrc: 150 },
      ],
      ytd: { real: 145, orc: 150, variance: -3.3, unit: 'm³/h' },
    },
    constrangimentos: [
      {
        facto: { pt: 'Britador primário com manutenção correctiva frequente', en: 'Primary crusher with frequent corrective maintenance' },
        efeito: { pt: 'Interrupção da alimentação da planta e atraso no ciclo de processamento', en: 'Plant feed interruption and processing cycle delay' },
        severidade: 'high'
      },
      {
        facto: { pt: 'Obstrução recorrente de crivos', en: 'Recurring screen blockage' },
        efeito: { pt: 'Redução de throughput e necessidade de paragens para limpeza', en: 'Throughput reduction and need for cleaning shutdowns' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Falhas de bombas de alimentação', en: 'Feed pump failures' },
        efeito: { pt: 'Interrupção do fluxo para DMS e perda de tempo produtivo', en: 'DMS flow interruption and loss of productive time' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Substituição de correias transportadoras', en: 'Conveyor belt replacement' },
        efeito: { pt: 'Paragens prolongadas para manutenção mecânica', en: 'Extended shutdowns for mechanical maintenance' },
        severidade: 'low'
      },
    ],
    ponteFinanceira: [
      {
        facto: { pt: 'Disponibilidade -2.8% vs plano', en: 'Availability -2.8% vs plan' },
        implicacaoOperacional: { pt: 'Menor tempo de operação dos activos críticos', en: 'Less operating time for critical assets' },
        leituraFinanceira: { pt: 'Risco de compressão de throughput — pressão moderada sobre capacidade de processamento', en: 'Throughput compression risk — moderate pressure on processing capacity' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Utilização efectiva -5.9% vs plano', en: 'Effective utilization -5.9% vs plan' },
        implicacaoOperacional: { pt: 'Capacidade instalada não convertida em produção útil', en: 'Installed capacity not converted to useful production' },
        leituraFinanceira: { pt: 'Pressão relevante sobre custo unitário — activos subutilizados diluem menos o custo fixo', en: 'Relevant pressure on unit cost — underutilized assets dilute less fixed cost' },
        severidade: 'high'
      },
      {
        facto: { pt: 'Horas de paragem +26% vs plano', en: 'Downtime hours +26% vs plan' },
        implicacaoOperacional: { pt: 'Tempo perdido acumulado superior ao tolerado', en: 'Accumulated lost time above tolerance' },
        leituraFinanceira: { pt: 'Pressão sobre previsibilidade e risco de execução do plano', en: 'Pressure on predictability and plan execution risk' },
        severidade: 'high'
      },
      {
        facto: { pt: 'Taxa de transferência DMS -3.3% vs plano', en: 'DMS transfer rate -3.3% vs plan' },
        implicacaoOperacional: { pt: 'Circuito DMS a operar ligeiramente abaixo da capacidade nominal', en: 'DMS circuit operating slightly below nominal capacity' },
        leituraFinanceira: { pt: 'Pressão limitada sobre throughput — desvio dentro da tolerância operacional', en: 'Limited pressure on throughput — deviation within operational tolerance' },
        severidade: 'low'
      },
    ],
  },
  2025: {
    period: '2025',
    keyMessages: [
      {
        iconKey: 'Gauge',
        iconBg: 'bg-gold-500/20',
        iconColor: 'text-gold-400',
        titleColor: 'text-gold-400',
        title: { pt: 'CAPACIDADE ÚTIL', en: 'USEFUL CAPACITY' },
        text: {
          pt: 'A eficiência operacional mede a capacidade dos activos de transformar capacidade instalada em produção útil. Disponibilidade sem utilização efectiva não protege o plano.',
          en: 'Operational efficiency measures the ability of assets to transform installed capacity into useful production. Availability without effective utilization does not protect the plan.'
        }
      },
      {
        iconKey: 'Clock',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'RESULTADO 2025', en: '2025 RESULT' },
        text: {
          pt: '2025 foi um ano de boa disciplina operacional, com disponibilidade e utilização alinhadas ao plano e paragens controladas.',
          en: '2025 was a year of good operational discipline, with availability and utilization aligned to plan and controlled shutdowns.'
        }
      },
      {
        iconKey: 'Target',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'FIABILIDADE', en: 'RELIABILITY' },
        text: {
          pt: 'Os circuitos críticos entregaram a fiabilidade necessária para suportar o plano operacional do ano.',
          en: 'Critical circuits delivered the reliability needed to support the annual operational plan.'
        }
      }
    ],
    kpis: {
      disponibilidadePlanta: { actual: 93.8, plan: 94.0, variance: -0.2, yoyVariance: 2.1, unit: '%', trend: 'stable' },
      utilizacaoEfetiva: { actual: 87.0, plan: 86.5, variance: 0.6, yoyVariance: 3.5, unit: '%', trend: 'up' },
      horasParagemPlanta: { actual: 480, plan: 500, variance: -4.0, yoyVariance: -8.2, unit: 'hrs', trend: 'down' },
      taxaTransferenciaDMS: { actual: 152, plan: 150, variance: 1.3, yoyVariance: 3.2, unit: 'm³/h', trend: 'up' },
    },
    disponibilidadeUtilizacao: {
      circuitos: [
        { nome: 'Pré-tratamento', disponibilidade: 94.5, utilizacao: 88.2, utilizacaoEfetiva: 83.4, status: 'conforme' },
        { nome: 'Lavaria Central', disponibilidade: 93.2, utilizacao: 86.5, utilizacaoEfetiva: 80.6, status: 'conforme' },
        { nome: 'DMS Principal', disponibilidade: 94.8, utilizacao: 89.5, utilizacaoEfetiva: 84.8, status: 'conforme' },
        { nome: 'Circuito de Recuperação', disponibilidade: 92.5, utilizacao: 84.2, utilizacaoEfetiva: 77.9, status: 'conforme' },
      ],
      leituraExecutiva: {
        pt: '2025 apresentou disponibilidade consistente em todos os circuitos críticos (>92%), com utilização efectiva entre 78-85%. A operação manteve disciplina operacional ao longo do ano.',
        en: '2025 showed consistent availability across all critical circuits (>92%), with effective utilization between 78-85%. Operations maintained operational discipline throughout the year.'
      }
    },
    paragensTempoPerdido: {
      ytd: {
        total: { real: 480, orc: 500, variance: -4.0, unit: 'hrs' },
        planeadas: { real: 200, orc: 210, variance: -4.8, unit: 'hrs' },
        naoPlaneadas: { real: 280, orc: 290, variance: -3.4, unit: 'hrs' },
      },
      leituraExecutiva: {
        pt: '2025 terminou com 480 horas de paragem (4% abaixo do plano), demonstrando boa disciplina de manutenção e gestão de paragens.',
        en: '2025 ended with 480 downtime hours (4% below plan), demonstrating good maintenance discipline and shutdown management.'
      }
    },
    taxaTransferenciaDMSData: {
      ytd: { real: 152, orc: 150, variance: 1.3, unit: 'm³/h' },
    },
    utilizacaoEfetivaCircuito: {
      ytd: { real: 87.0, orc: 86.5, variance: 0.6, unit: '%' },
    },
    ponteFinanceira: [
      {
        facto: { pt: 'Disponibilidade alinhada ao plano (-0.2%)', en: 'Availability aligned to plan (-0.2%)' },
        implicacaoOperacional: { pt: 'Tempo de operação conforme esperado', en: 'Operating time as expected' },
        leituraFinanceira: { pt: 'Pressão limitada — sem impacto relevante sobre throughput', en: 'Limited pressure — no relevant impact on throughput' },
        severidade: 'low'
      },
      {
        facto: { pt: 'Utilização efectiva +0.6% vs plano', en: 'Effective utilization +0.6% vs plan' },
        implicacaoOperacional: { pt: 'Capacidade instalada bem aproveitada', en: 'Installed capacity well utilized' },
        leituraFinanceira: { pt: 'Contribuição positiva para diluição de custo fixo', en: 'Positive contribution to fixed cost dilution' },
        severidade: 'low'
      },
      {
        facto: { pt: 'Horas de paragem -4% vs plano', en: 'Downtime hours -4% vs plan' },
        implicacaoOperacional: { pt: 'Tempo perdido controlado', en: 'Lost time controlled' },
        leituraFinanceira: { pt: 'Boa previsibilidade operacional e disciplina de execução', en: 'Good operational predictability and execution discipline' },
        severidade: 'low'
      },
    ],
  },
};

// ============================================================================
// RISK DELIVERY DATA
// ============================================================================
export const riskDeliveryTrajectoryKpis = {
  2026: [
    {
      key: 'quilatesRecuperados',
      label: { pt: 'Quilates Recuperados', en: 'Carats Recovered' },
      unit: 'Kcts',
      rollingData: [
        { month: 'Abr 25', value: 5.8 }, { month: 'Mai 25', value: 5.95 }, { month: 'Jun 25', value: 6.1 },
        { month: 'Jul 25', value: 5.7 }, { month: 'Ago 25', value: 5.85 }, { month: 'Set 25', value: 6.2 },
        { month: 'Out 25', value: 5.9 }, { month: 'Nov 25', value: 6.05 }, { month: 'Dez 25', value: 5.65 },
        { month: 'Jan 26', value: 5.5 }, { month: 'Fev 26', value: 5.7 }, { month: 'Mar 26', value: 5.8 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 6.0 }, { quarter: '3T26', value: 5.8 }, { quarter: '4T26', value: 5.6 }],
      q1Actual: 5.67,
      trend: 'down',
    },
    {
      key: 'gapVsOrcamento',
      label: { pt: 'Gap vs Orçamento', en: 'Gap vs Budget' },
      unit: '%',
      rollingData: [
        { month: 'Abr 25', value: -2.1 }, { month: 'Mai 25', value: -1.5 }, { month: 'Jun 25', value: 0.5 },
        { month: 'Jul 25', value: -4.2 }, { month: 'Ago 25', value: -2.8 }, { month: 'Set 25', value: 1.2 },
        { month: 'Out 25', value: -3.1 }, { month: 'Nov 25', value: -1.8 }, { month: 'Dez 25', value: -5.5 },
        { month: 'Jan 26', value: -8.5 }, { month: 'Fev 26', value: -6.2 }, { month: 'Mar 26', value: -8.1 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 0 }, { quarter: '3T26', value: 0 }, { quarter: '4T26', value: 0 }],
      q1Actual: -7.6,
      trend: 'down',
    },
    {
      key: 'volumeNaoProdutivo',
      label: { pt: 'Volume Não Produtivo', en: 'Non-Productive Volume' },
      unit: 'K m³',
      rollingData: [
        { month: 'Abr 25', value: 145 }, { month: 'Mai 25', value: 152 }, { month: 'Jun 25', value: 148 },
        { month: 'Jul 25', value: 165 }, { month: 'Ago 25', value: 158 }, { month: 'Set 25', value: 142 },
        { month: 'Out 25', value: 155 }, { month: 'Nov 25', value: 162 }, { month: 'Dez 25', value: 170 },
        { month: 'Jan 26', value: 155 }, { month: 'Fev 26', value: 158 }, { month: 'Mar 26', value: 156 },
      ],
      budgetQuarters: [{ quarter: '2T26', value: 140 }, { quarter: '3T26', value: 138 }, { quarter: '4T26', value: 145 }],
      q1Actual: 156,
      trend: 'up',
    },
  ],
  2025: [
    {
      key: 'quilatesRecuperados',
      label: { pt: 'Quilates Recuperados', en: 'Carats Recovered' },
      unit: 'Kcts',
      rollingData: [
        { month: 'Jan', value: 5.2 }, { month: 'Fev', value: 5.4 }, { month: 'Mar', value: 5.6 },
        { month: 'Abr', value: 5.8 }, { month: 'Mai', value: 5.95 }, { month: 'Jun', value: 6.0 },
        { month: 'Jul', value: 5.7 }, { month: 'Ago', value: 5.85 }, { month: 'Set', value: 6.2 },
        { month: 'Out', value: 5.9 }, { month: 'Nov', value: 6.05 }, { month: 'Dez', value: 5.65 },
      ],
      priorYearData: [
        { month: 'Jan', value: 5.0 }, { month: 'Fev', value: 5.15 }, { month: 'Mar', value: 5.3 },
        { month: 'Abr', value: 5.5 }, { month: 'Mai', value: 5.6 }, { month: 'Jun', value: 5.7 },
        { month: 'Jul', value: 5.4 }, { month: 'Ago', value: 5.55 }, { month: 'Set', value: 5.85 },
        { month: 'Out', value: 5.6 }, { month: 'Nov', value: 5.75 }, { month: 'Dez', value: 5.4 },
      ],
      trend: 'up',
    },
    {
      key: 'gapVsOrcamento',
      label: { pt: 'Gap vs Orçamento', en: 'Gap vs Budget' },
      unit: '%',
      rollingData: [
        { month: 'Jan', value: -3.5 }, { month: 'Fev', value: -2.8 }, { month: 'Mar', value: -1.2 },
        { month: 'Abr', value: -2.1 }, { month: 'Mai', value: -1.5 }, { month: 'Jun', value: 0.5 },
        { month: 'Jul', value: -4.2 }, { month: 'Ago', value: -2.8 }, { month: 'Set', value: 1.2 },
        { month: 'Out', value: -3.1 }, { month: 'Nov', value: -1.8 }, { month: 'Dez', value: -5.5 },
      ],
      priorYearData: [
        { month: 'Jan', value: -5.0 }, { month: 'Fev', value: -4.2 }, { month: 'Mar', value: -3.5 },
        { month: 'Abr', value: -4.8 }, { month: 'Mai', value: -3.8 }, { month: 'Jun', value: -2.5 },
        { month: 'Jul', value: -6.0 }, { month: 'Ago', value: -4.5 }, { month: 'Set', value: -1.8 },
        { month: 'Out', value: -5.2 }, { month: 'Nov', value: -3.5 }, { month: 'Dez', value: -7.0 },
      ],
      trend: 'up',
    },
    {
      key: 'volumeNaoProdutivo',
      label: { pt: 'Volume Não Produtivo', en: 'Non-Productive Volume' },
      unit: 'K m³',
      rollingData: [
        { month: 'Jan', value: 140 }, { month: 'Fev', value: 142 }, { month: 'Mar', value: 145 },
        { month: 'Abr', value: 145 }, { month: 'Mai', value: 152 }, { month: 'Jun', value: 148 },
        { month: 'Jul', value: 165 }, { month: 'Ago', value: 158 }, { month: 'Set', value: 142 },
        { month: 'Out', value: 155 }, { month: 'Nov', value: 162 }, { month: 'Dez', value: 170 },
      ],
      priorYearData: [
        { month: 'Jan', value: 130 }, { month: 'Fev', value: 132 }, { month: 'Mar', value: 135 },
        { month: 'Abr', value: 138 }, { month: 'Mai', value: 142 }, { month: 'Jun', value: 140 },
        { month: 'Jul', value: 155 }, { month: 'Ago', value: 148 }, { month: 'Set', value: 135 },
        { month: 'Out', value: 145 }, { month: 'Nov', value: 152 }, { month: 'Dez', value: 160 },
      ],
      trend: 'up',
    },
  ],
};

export const riskDeliveryData = {
  2026: {
    period: '1T 2026',
    keyMessages: [
      {
        iconKey: 'AlertTriangle',
        iconBg: 'bg-amber-500/20',
        iconColor: 'text-amber-400',
        titleColor: 'text-amber-400',
        title: { pt: 'SINAIS DE FRAGILIDADE', en: 'FRAGILITY SIGNALS' },
        text: {
          pt: 'O cumprimento aparente de alguns indicadores pode esconder fragilidades estruturais do plano. Gap de quilates persistente (-8.1% vs orç.) sinaliza pressão sobre entrega física.',
          en: 'Apparent compliance of some indicators may hide structural plan fragilities. Persistent carat gap (-8.1% vs budget) signals pressure on physical delivery.'
        }
      },
      {
        iconKey: 'Eye',
        iconBg: 'bg-blue-500/20',
        iconColor: 'text-blue-400',
        titleColor: 'text-blue-400',
        title: { pt: 'ANTECIPAR RISCO', en: 'ANTICIPATE RISK' },
        text: {
          pt: 'O principal valor desta página é antecipar risco antes de ele se reflectir integralmente em resultados. Desvios actuais podem comprometer previsibilidade futura.',
          en: 'The main value of this page is anticipating risk before it fully reflects in results. Current deviations may compromise future predictability.'
        }
      },
      {
        iconKey: 'Target',
        iconBg: 'bg-rose-500/20',
        iconColor: 'text-rose-400',
        titleColor: 'text-rose-400',
        title: { pt: 'PRESSÃO SOBRE ENTREGA', en: 'DELIVERY PRESSURE' },
        text: {
          pt: 'Para o CFO, esta página deve mostrar se os desvios operacionais já observados colocam pressão relevante sobre entrega, custo, previsibilidade e caixa.',
          en: 'For the CFO, this page should show if observed operational deviations place relevant pressure on delivery, cost, predictability and cash.'
        }
      }
    ],
    kpis: {
      quilatesRecuperados: { actual: 17000, plan: 18500, variance: -8.1, yoyVariance: -2.3, unit: 'cts', trend: 'down' },
      volumeTotalDMS: { actual: 323, plan: 350, variance: -7.7, yoyVariance: -5.2, unit: 'K m³', trend: 'down' },
      gapQuilatesVsOrc: { actual: -1500, plan: 0, variance: -8.1, yoyVariance: -3.5, unit: 'cts', trend: 'down' },
      volumeNaoProdutivo: { actual: 44, plan: 30, variance: 46.7, yoyVariance: 22.0, unit: 'K m³', trend: 'up' },
    },
    sinaisEntrega: {
      monthly: [
        { month: 'Jan', quilatesReal: 5500, quilatesOrc: 6000, dmsReal: 105, dmsOrc: 115 },
        { month: 'Fev', quilatesReal: 5700, quilatesOrc: 6100, dmsReal: 110, dmsOrc: 117 },
        { month: 'Mar', quilatesReal: 5800, quilatesOrc: 6400, dmsReal: 108, dmsOrc: 118 },
      ],
      ytd: {
        quilates: { real: 17000, orc: 18500, variance: -8.1 },
        dms: { real: 323, orc: 350, variance: -7.7 },
      },
      leituraExecutiva: {
        pt: 'O 1T 2026 apresenta um gap persistente face ao plano: quilates recuperados -8.1% e volume DMS -7.7%. A tendência mensal mostra desvios consistentes em todos os meses, sem sinais de recuperação. O plano está a ser entregue parcialmente, não com consistência.',
        en: 'Q1 2026 shows a persistent gap vs plan: recovered carats -8.1% and DMS volume -7.7%. Monthly trend shows consistent deviations across all months, with no recovery signs. The plan is being delivered partially, not consistently.'
      }
    },
    desviosMateriais: [
      {
        tema: { pt: 'Gap de quilates recuperados persistente', en: 'Persistent recovered carats gap' },
        desvio: '-8.1%',
        evidencia: { pt: '1,500 cts abaixo do plano no 1T26', en: '1,500 cts below plan in Q1 26' },
        materialidade: 'high',
        implicacao: { pt: 'Menor base física de entrega — pressão directa sobre receita', en: 'Lower physical delivery base — direct pressure on revenue' }
      },
      {
        tema: { pt: 'Volume DMS abaixo do plano', en: 'DMS volume below plan' },
        desvio: '-7.7%',
        evidencia: { pt: '27 K m³ abaixo do plano no 1T26', en: '27 K m³ below plan in Q1 26' },
        materialidade: 'high',
        implicacao: { pt: 'Menor capacidade útil de processamento — risco de throughput', en: 'Lower useful processing capacity — throughput risk' }
      },
      {
        tema: { pt: 'Volume não produtivo elevado', en: 'High non-productive volume' },
        desvio: '+46.7%',
        evidencia: { pt: '44 K m³ vs 30 K m³ orçamentado', en: '44 K m³ vs 30 K m³ budgeted' },
        materialidade: 'medium',
        implicacao: { pt: 'Pressão sobre eficiência e custo unitário', en: 'Pressure on efficiency and unit cost' }
      },
      {
        tema: { pt: 'Teor de alimentação -2.2% vs plano', en: 'Feed grade -2.2% vs plan' },
        desvio: '-2.2%',
        evidencia: { pt: '15.74 cpht vs 16.1 cpht orçamentado', en: '15.74 cpht vs 16.1 cpht budgeted' },
        materialidade: 'medium',
        implicacao: { pt: 'Menor conversão por tonelada tratada', en: 'Lower conversion per treated ton' }
      },
    ],
    fragilidades: [
      {
        tipo: { pt: 'Dependência de stockpile elevada', en: 'High stockpile dependency' },
        sinal: { pt: '17% da alimentação proveniente de stockpile (vs 12% plano)', en: '17% of feed from stockpile (vs 12% plan)' },
        risco: 'medium',
        implicacao: { pt: 'Menor flexibilidade operacional — stock a ser consumido mais rapidamente', en: 'Lower operational flexibility — stock being consumed faster' }
      },
      {
        tipo: { pt: 'Concentração de produção em frentes limitadas', en: 'Production concentration in limited fronts' },
        sinal: { pt: '65% da produção concentrada em 2 das 4 frentes', en: '65% of production concentrated in 2 of 4 fronts' },
        risco: 'medium',
        implicacao: { pt: 'Maior vulnerabilidade a paragens em frentes críticas', en: 'Higher vulnerability to shutdowns in critical fronts' }
      },
      {
        tipo: { pt: 'Horas de paragem acima do plano', en: 'Downtime hours above plan' },
        sinal: { pt: '142 hrs vs 110 hrs plano (+29%)', en: '142 hrs vs 110 hrs plan (+29%)' },
        risco: 'high',
        implicacao: { pt: 'Perda de capacidade útil — risco de não recuperação no restante do ano', en: 'Loss of useful capacity — risk of non-recovery in remaining year' }
      },
    ],
    watchlist: [
      {
        risco: { pt: 'Gap de quilates persistente sem tendência de recuperação', en: 'Persistent carat gap with no recovery trend' },
        severidade: 'high',
        evidencia: { pt: 'Desvio -8.1% no 1T26, consistente em todos os meses', en: 'Deviation -8.1% in Q1 26, consistent across all months' },
        implicacaoOperacional: { pt: 'Menor entrega física — pressão sobre vendas e comercial', en: 'Lower physical delivery — pressure on sales and commercial' },
        implicacaoFinanceira: { pt: 'Pressão relevante sobre receita e previsibilidade', en: 'Relevant pressure on revenue and predictability' },
        mitigacao: { pt: 'Revisão do plano de alimentação e priorização de frentes de maior teor', en: 'Feed plan review and prioritization of higher grade fronts' }
      },
      {
        risco: { pt: 'Volume não produtivo elevado e em tendência de aumento', en: 'High non-productive volume with increasing trend' },
        severidade: 'medium',
        evidencia: { pt: '+46.7% vs plano no 1T26', en: '+46.7% vs plan in Q1 26' },
        implicacaoOperacional: { pt: 'Desperdício de capacidade e ineficiência operacional', en: 'Capacity waste and operational inefficiency' },
        implicacaoFinanceira: { pt: 'Pressão moderada sobre custo unitário', en: 'Moderate pressure on unit cost' },
        mitigacao: { pt: 'Análise de causas de reprocessamento e ajuste de circuitos', en: 'Reprocessing cause analysis and circuit adjustment' }
      },
      {
        risco: { pt: 'Dependência crescente de stockpile para alimentação', en: 'Growing stockpile dependency for feed' },
        severidade: 'medium',
        evidencia: { pt: '17% da alimentação vs 12% plano', en: '17% of feed vs 12% plan' },
        implicacaoOperacional: { pt: 'Menor buffer operacional e flexibilidade reduzida', en: 'Lower operational buffer and reduced flexibility' },
        implicacaoFinanceira: { pt: 'Risco sobre continuidade operacional no 2T26', en: 'Risk on operational continuity in Q2 26' },
        mitigacao: { pt: 'Acelerar abertura de novas frentes de mina', en: 'Accelerate opening of new mine fronts' }
      },
    ],
    ponteRisco: [
      {
        facto: { pt: 'Quilates recuperados -8.1% vs plano', en: 'Recovered carats -8.1% vs plan' },
        implicacaoOperacional: { pt: 'Menor base física de entrega ao comercial', en: 'Lower physical delivery base to commercial' },
        leituraRisco: { pt: 'Risco relevante sobre cumprimento de vendas e receita', en: 'Relevant risk on sales fulfillment and revenue' },
        severidade: 'high'
      },
      {
        facto: { pt: 'Volume DMS -7.7% vs plano', en: 'DMS volume -7.7% vs plan' },
        implicacaoOperacional: { pt: 'Menor throughput efectivo do circuito de processamento', en: 'Lower effective throughput of processing circuit' },
        leituraRisco: { pt: 'Risco moderado de compressão de capacidade no restante do ano', en: 'Moderate risk of capacity compression in remaining year' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Volume não produtivo +46.7% vs plano', en: 'Non-productive volume +46.7% vs plan' },
        implicacaoOperacional: { pt: 'Desperdício de capacidade operacional em reprocessamento', en: 'Operational capacity waste in reprocessing' },
        leituraRisco: { pt: 'Pressão moderada sobre custo unitário e eficiência', en: 'Moderate pressure on unit cost and efficiency' },
        severidade: 'medium'
      },
      {
        facto: { pt: 'Horas de paragem +29% vs plano', en: 'Downtime hours +29% vs plan' },
        implicacaoOperacional: { pt: 'Perda de tempo produtivo acumulada', en: 'Accumulated productive time loss' },
        leituraRisco: { pt: 'Risco sobre previsibilidade e execução do plano anual', en: 'Risk on predictability and annual plan execution' },
        severidade: 'high'
      },
    ],
  },
  2025: {
    period: '2025',
    keyMessages: [
      {
        iconKey: 'Target',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'ENTREGA CONSISTENTE', en: 'CONSISTENT DELIVERY' },
        text: {
          pt: '2025 foi um ano de entrega sólida com quilates recuperados apenas -2.3% vs plano. O plano foi executado com robustez operacional.',
          en: '2025 was a solid delivery year with recovered carats only -2.3% vs plan. The plan was executed with operational robustness.'
        }
      },
      {
        iconKey: 'Shield',
        iconBg: 'bg-emerald-500/20',
        iconColor: 'text-emerald-400',
        titleColor: 'text-emerald-400',
        title: { pt: 'DISCIPLINA OPERACIONAL', en: 'OPERATIONAL DISCIPLINE' },
        text: {
          pt: 'Volume DMS alinhado ao plano e volume não produtivo controlado demonstraram boa disciplina operacional ao longo do ano.',
          en: 'DMS volume aligned to plan and controlled non-productive volume demonstrated good operational discipline throughout the year.'
        }
      },
      {
        iconKey: 'BarChart3',
        iconBg: 'bg-blue-500/20',
        iconColor: 'text-blue-400',
        titleColor: 'text-blue-400',
        title: { pt: 'BASE PARA 2026', en: '2026 BASELINE' },
        text: {
          pt: 'Os resultados de 2025 servem de base comparativa para 2026. O desvio actual do 1T26 contrasta com a consistência de 2025.',
          en: '2025 results serve as comparative baseline for 2026. Current Q1 26 deviation contrasts with 2025 consistency.'
        }
      }
    ],
    kpis: {
      quilatesRecuperados: { actual: 70500, plan: 72000, variance: -2.1, yoyVariance: 3.5, unit: 'cts', trend: 'stable' },
      volumeTotalDMS: { actual: 1420, plan: 1450, variance: -2.1, yoyVariance: 2.8, unit: 'K m³', trend: 'stable' },
      gapQuilatesVsOrc: { actual: -1500, plan: 0, variance: -2.1, yoyVariance: 0, unit: 'cts', trend: 'stable' },
      volumeNaoProdutivo: { actual: 140, plan: 150, variance: -6.7, yoyVariance: -8.2, unit: 'K m³', trend: 'down' },
    },
    sinaisEntrega: {
      leituraExecutiva: {
        pt: '2025 apresentou entrega consistente com desvios limitados face ao plano. Os quilates recuperados ficaram apenas -2.1% abaixo do orçamento, demonstrando robustez operacional e boa execução do plano.',
        en: '2025 showed consistent delivery with limited deviations vs plan. Recovered carats were only -2.1% below budget, demonstrating operational robustness and good plan execution.'
      }
    },
    ponteRisco: [
      {
        facto: { pt: 'Quilates recuperados -2.1% vs plano', en: 'Recovered carats -2.1% vs plan' },
        implicacaoOperacional: { pt: 'Desvio dentro da tolerância operacional', en: 'Deviation within operational tolerance' },
        leituraRisco: { pt: 'Pressão limitada sobre entrega e receita', en: 'Limited pressure on delivery and revenue' },
        severidade: 'low'
      },
      {
        facto: { pt: 'Volume não produtivo -6.7% vs plano', en: 'Non-productive volume -6.7% vs plan' },
        implicacaoOperacional: { pt: 'Boa eficiência operacional', en: 'Good operational efficiency' },
        leituraRisco: { pt: 'Contribuição positiva para custo unitário', en: 'Positive contribution to unit cost' },
        severidade: 'low'
      },
    ],
  },
};

// Export all operations data modules
export default {
  mineAccess: {
    trajectoryKpis: mineAccessTrajectoryKpis,
    data: mineAccessData,
  },
  oreStocks: {
    trajectoryKpis: oreStocksTrajectoryKpis,
    data: oreStocksData,
  },
  plantRecovery: {
    trajectoryKpis: plantRecoveryTrajectoryKpis,
    data: plantRecoveryData,
  },
  productionRevenue: {
    trajectoryKpis: productionRevenueTrajectoryKpis,
    data: productionRevenueData,
  },
  efficiency: {
    trajectoryKpis: efficiencyTrajectoryKpis,
    data: efficiencyData,
  },
  riskDelivery: {
    trajectoryKpis: riskDeliveryTrajectoryKpis,
    data: riskDeliveryData,
  },
};
